// src/auth/access-control.ts
var ROLE_CODES = ["ADMIN", "FINANCE", "BRANCH_MANAGER", "VIEWER"];
var PERMISSIONS = [
  "companies:read",
  "companies:manage",
  "ledger-accounts:read",
  "ledger-accounts:manage",
  "cost-centers:read",
  "cost-centers:manage",
  "bank-accounts:read",
  "bank-accounts:manage",
  "bank-reconciliation:read",
  "bank-reconciliation:manage",
  "ledger-entries:read",
  "ledger-entries:create",
  "parties:read",
  "parties:create",
  "parties:manage",
  "titles:read",
  "titles:create",
  "titles:manage",
  "accounting-periods:read",
  "accounting-periods:close",
  "accounting-periods:reopen",
  "income-statement:read",
  "cash-flow:read",
  "users:manage"
];
var ROLE_NAMES = {
  ADMIN: "Administrador",
  FINANCE: "Financeiro",
  BRANCH_MANAGER: "Gestor de filial",
  VIEWER: "Leitor"
};
var ORGANIZATION_WIDE_PERMISSIONS = [
  "companies:manage",
  "ledger-accounts:manage",
  "cost-centers:manage",
  "parties:manage",
  "users:manage"
];
var REPORTS = ["income-statement:read", "cash-flow:read"];
var ENTRIES = ["ledger-entries:read", "ledger-entries:create"];
var TITLES = ["titles:read", "titles:create"];
var TITLE_MANAGEMENT = ["titles:manage"];
var PERIOD_CLOSING = [
  "accounting-periods:read",
  "accounting-periods:close"
];
var RECONCILIATION = [
  "bank-reconciliation:read",
  "bank-reconciliation:manage"
];
var PARTY_CREATION = ["parties:create"];
var CATALOG_READS = [
  "companies:read",
  "ledger-accounts:read",
  "cost-centers:read",
  "bank-accounts:read",
  "parties:read"
];
var ROLE_PERMISSIONS = {
  ADMIN: PERMISSIONS,
  FINANCE: [
    ...CATALOG_READS,
    "ledger-accounts:manage",
    "cost-centers:manage",
    "bank-accounts:manage",
    ...RECONCILIATION,
    ...PARTY_CREATION,
    "parties:manage",
    ...ENTRIES,
    ...TITLES,
    ...TITLE_MANAGEMENT,
    ...PERIOD_CLOSING,
    ...REPORTS
  ],
  BRANCH_MANAGER: [
    ...CATALOG_READS,
    "bank-reconciliation:read",
    ...PARTY_CREATION,
    ...ENTRIES,
    ...TITLES,
    "accounting-periods:read",
    ...REPORTS
  ],
  VIEWER: ["companies:read", ...REPORTS]
};

// src/auth/permission-coverage.ts
var PERMISSIONS_BY_ROLE = new Map(
  Object.entries(ROLE_PERMISSIONS).map(([code, permissions]) => [
    code,
    new Set(permissions)
  ])
);
var ORGANIZATION_WIDE = new Set(ORGANIZATION_WIDE_PERMISSIONS);
function grants(role, permission) {
  if (PERMISSIONS_BY_ROLE.get(role.code)?.has(permission) !== true) return false;
  return role.companyId === null || !ORGANIZATION_WIDE.has(permission);
}
function permissionCoverage(roles, permission) {
  const granting = roles.filter((role) => grants(role, permission));
  if (granting.some((role) => role.companyId === null)) return { kind: "ORGANIZATION" };
  const companyIds = /* @__PURE__ */ new Set();
  for (const role of granting) {
    if (role.companyId !== null) companyIds.add(role.companyId);
  }
  return companyIds.size > 0 ? { kind: "COMPANIES", companyIds } : { kind: "NONE" };
}
function coversAnyCompany(coverage, selection) {
  if (coverage.kind === "NONE") return false;
  if (selection === null) return true;
  if (coverage.kind === "ORGANIZATION") return selection.length > 0;
  return selection.some((companyId) => coverage.companyIds.has(companyId));
}

// src/audit/audit-event.ts
import { z as z4 } from "zod";

// src/primitives/identifiers.ts
import { z } from "zod";
var uuidSchema = z.uuid().meta({ example: "0199b4c8-6f1e-7c3a-9a51-3f2a1c0d9e10" });
var isoDateSchema = z.iso.date().meta({ example: "2026-10-05" });
var isoDateTimeSchema = z.iso.datetime().meta({ example: "2026-10-05T13:45:00.000Z" });

// src/primitives/pagination.ts
import { z as z2 } from "zod";
var MAX_PAGE_SIZE = 100;
var DEFAULT_PAGE_SIZE = 20;
var paginationQuerySchema = z2.object({
  page: z2.coerce.number().int().min(1).default(1),
  pageSize: z2.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE)
});
var sortOrderSchema = z2.enum(["asc", "desc"]);
function paginatedResponseSchema(item) {
  return z2.object({
    items: z2.array(item),
    total: z2.number().int().min(0),
    page: z2.number().int().min(1),
    pageSize: z2.number().int().min(1).max(MAX_PAGE_SIZE)
  });
}

// src/primitives/year-month.ts
import { z as z3 } from "zod";
var yearMonthSchema = z3.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "M\xEAs inv\xE1lido; use o formato AAAA-MM").meta({ example: "2026-09" });
function monthSpan(from, to) {
  const index = (value) => Number(value.slice(0, 4)) * 12 + Number(value.slice(5, 7));
  return index(to) - index(from) + 1;
}

// src/audit/audit-event.ts
var auditActorSchema = z4.object({ userId: uuidSchema, name: z4.string() });
var auditEventBaseShape = {
  id: uuidSchema,
  occurredAt: isoDateTimeSchema,
  actor: auditActorSchema,
  entityId: uuidSchema,
  companyId: uuidSchema.nullable()
};
var auditFieldChangeSchema = z4.object({
  field: z4.string(),
  before: z4.string().nullable(),
  after: z4.string().nullable()
});
var auditEventSchema = z4.discriminatedUnion("action", [
  z4.object({
    ...auditEventBaseShape,
    action: z4.literal("ACCOUNTING_PERIOD_CLOSED"),
    entityType: z4.literal("COMPANY_PERIOD_CLOSING"),
    details: z4.object({
      closedThroughBefore: yearMonthSchema.nullable(),
      closedThroughAfter: yearMonthSchema
    })
  }),
  z4.object({
    ...auditEventBaseShape,
    action: z4.literal("ACCOUNTING_PERIOD_REOPENED"),
    entityType: z4.literal("COMPANY_PERIOD_CLOSING"),
    details: z4.object({
      closedThroughBefore: yearMonthSchema,
      closedThroughAfter: yearMonthSchema.nullable(),
      justification: z4.string()
    })
  }),
  z4.object({
    ...auditEventBaseShape,
    action: z4.literal("TITLE_UPDATED"),
    entityType: z4.literal("TITLE"),
    details: z4.object({ changes: z4.array(auditFieldChangeSchema) })
  }),
  z4.object({
    ...auditEventBaseShape,
    action: z4.literal("TITLE_CANCELLED"),
    entityType: z4.literal("TITLE"),
    details: z4.object({ reason: z4.string() })
  })
]);
var auditActionSchema = z4.enum([
  "ACCOUNTING_PERIOD_CLOSED",
  "ACCOUNTING_PERIOD_REOPENED",
  "TITLE_UPDATED",
  "TITLE_CANCELLED"
]);
var auditHistoryQuerySchema = paginationQuerySchema;
var auditEventListSchema = paginatedResponseSchema(auditEventSchema);

// src/auth/session.ts
import { z as z6 } from "zod";

// src/enums/index.ts
import { z as z5 } from "zod";
var recordStatusSchema = z5.enum(["ACTIVE", "INACTIVE"]);
var companyKindSchema = z5.enum(["HEADQUARTERS", "BRANCH"]);
var taxRegimeSchema = z5.enum(["SIMPLIFIED", "PRESUMED_PROFIT", "ACTUAL_PROFIT"]);
var cnpjRegistrationStatusSchema = z5.enum([
  "ACTIVE",
  "SUSPENDED",
  "UNFIT",
  "CLOSED",
  "VOID"
]);
var BRAZILIAN_STATES = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO"
];
var brazilianStateSchema = z5.enum(BRAZILIAN_STATES, { message: "UF inv\xE1lida" });
var ledgerAccountCategorySchema = z5.enum([
  "ASSET",
  "LIABILITY",
  "EQUITY",
  "REVENUE",
  "EXPENSE"
]);
var ledgerNatureSchema = z5.enum(["DEBIT", "CREDIT"]);
var ledgerAccountKindSchema = z5.enum(["SYNTHETIC", "ANALYTIC"]);
var bankAccountKindSchema = z5.enum([
  "CHECKING",
  "SAVINGS",
  "PAYMENT",
  "INVESTMENT",
  "CASH",
  "CREDIT_CARD"
]);
var entryDirectionSchema = z5.enum(["DEBIT", "CREDIT"]);
var partyKindSchema = z5.enum(["CUSTOMER", "SUPPLIER", "BOTH"]);
var titleKindSchema = z5.enum(["PAYABLE", "RECEIVABLE"]);
var titleStatusSchema = z5.enum(["OPEN", "SETTLED", "CANCELLED"]);
var titleStatusFilterSchema = z5.enum(["OPEN", "OVERDUE", "SETTLED", "CANCELLED"]);
var titleDateFieldSchema = z5.enum(["DUE", "COMPETENCE", "SETTLEMENT"]);
var recurrenceFrequencySchema = z5.enum(["WEEKLY", "MONTHLY", "QUARTERLY", "YEARLY"]);
var allocationMethodSchema = z5.enum(["PERCENTAGE", "AMOUNT"]);
var incomeStatementLineSchema = z5.enum([
  "GROSS_REVENUE",
  "REVENUE_DEDUCTIONS",
  "COSTS",
  "OPERATING_EXPENSES",
  "FINANCIAL_RESULT",
  "INCOME_TAXES"
]);
var ledgerAccountTypeSchema = z5.enum([
  "REVENUE",
  "DEDUCTION",
  "COST",
  "EXPENSE",
  "FINANCIAL_REVENUE",
  "FINANCIAL_EXPENSE",
  "ASSET",
  "LIABILITY",
  "EQUITY"
]);
var balanceSheetGroupSchema = z5.enum([
  "CURRENT_ASSETS",
  "LONG_TERM_ASSETS",
  "INVESTMENTS",
  "PROPERTY_PLANT_AND_EQUIPMENT",
  "INTANGIBLE_ASSETS",
  "CURRENT_LIABILITIES",
  "NON_CURRENT_LIABILITIES",
  "SHARE_CAPITAL",
  "CAPITAL_RESERVES",
  "VALUATION_ADJUSTMENTS",
  "PROFIT_RESERVES",
  "TREASURY_SHARES",
  "ACCUMULATED_PROFIT_OR_LOSS"
]);
var ledgerAccountMappingIssueSchema = z5.enum([
  "MISSING_ACCOUNT_TYPE",
  "MISSING_MAPPING",
  "INCOMPATIBLE_MAPPING"
]);
var chartTemplateCodeSchema = z5.enum([
  "PME_SIMPLIFIED",
  "PME_PRESUMED_PROFIT",
  "PME_ACTUAL_PROFIT"
]);
var accountingBasisSchema = z5.enum(["ACCRUAL", "CASH"]);
var statementPeriodSchema = z5.enum(["MONTH", "QUARTER", "YEAR", "YEAR_TO_DATE"]);
var importTemplateKindSchema = z5.enum(["TITLES"]);
var bankStatementLineStatusSchema = z5.enum(["PENDING", "RECONCILED", "IGNORED"]);
var roleCodeSchema = z5.enum(ROLE_CODES);

// src/auth/session.ts
var permissionSchema = z6.enum(PERMISSIONS);
var sessionRoleSchema = z6.object({
  code: roleCodeSchema,
  companyId: uuidSchema.nullable()
});
var sessionSummarySchema = z6.object({
  userId: uuidSchema,
  organizationId: uuidSchema.nullable(),
  displayName: z6.string(),
  roles: z6.array(sessionRoleSchema),
  permissions: z6.array(permissionSchema),
  /** Admin geral da plataforma: cria organizações e os administradores delas. */
  platformAdmin: z6.boolean()
});
var authSessionResponseSchema = z6.object({
  accessToken: z6.string().min(1),
  tokenType: z6.literal("Bearer"),
  expiresAt: isoDateTimeSchema,
  session: sessionSummarySchema
});
var PASSWORD_MIN_LENGTH = 12;
var PASSWORD_MAX_LENGTH = 128;
var MAX_PRESENTED_SECRET_LENGTH = 1024;
var emailInputSchema = z6.string().trim().min(1, "Informe o e-mail").max(254);
var codePointLength = (value) => Array.from(value).length;
var newPasswordSchema = z6.string().refine((value) => codePointLength(value) >= PASSWORD_MIN_LENGTH, {
  message: `A senha deve ter pelo menos ${String(PASSWORD_MIN_LENGTH)} caracteres`
}).refine((value) => codePointLength(value) <= PASSWORD_MAX_LENGTH, {
  message: `A senha deve ter no m\xE1ximo ${String(PASSWORD_MAX_LENGTH)} caracteres`
}).meta({ minLength: PASSWORD_MIN_LENGTH, maxLength: PASSWORD_MAX_LENGTH });
var loginRequestSchema = z6.object({
  email: emailInputSchema,
  password: z6.string().min(1, "Informe a senha").max(MAX_PRESENTED_SECRET_LENGTH)
});
var changePasswordRequestSchema = z6.object({
  currentPassword: z6.string().min(1, "Informe a senha atual").max(MAX_PRESENTED_SECRET_LENGTH),
  newPassword: newPasswordSchema
});
var passwordResetRequestSchema = z6.object({ email: emailInputSchema });
var passwordResetConfirmSchema = z6.object({
  token: z6.string().min(1).max(MAX_PRESENTED_SECRET_LENGTH),
  newPassword: newPasswordSchema
});
var ORGANIZATION_HEADER_NAME = "X-Organization-Id";
var CSRF_HEADER_NAME = "X-Requested-With";
var CSRF_HEADER_VALUE = "jaf-web";

// src/company/company.ts
import { z as z12 } from "zod";

// src/primitives/address.ts
import { z as z8 } from "zod";

// src/primitives/postal-code.ts
import { z as z7 } from "zod";
var POSTAL_CODE_FORMAT = /^\d{8}$/;
function isValidPostalCode(value) {
  return POSTAL_CODE_FORMAT.test(value);
}
var postalCodeSchema = z7.string().refine(isValidPostalCode, { message: "CEP inv\xE1lido: informe os 8 d\xEDgitos, sem m\xE1scara" }).meta({ description: "CEP com 8 d\xEDgitos, sem m\xE1scara", example: "01310100" });

// src/primitives/address.ts
var ADDRESS_LIMITS = {
  street: 200,
  number: 20,
  complement: 100,
  district: 100,
  city: 100
};
var requiredText = (max) => z8.string().trim().min(1, { message: "Campo obrigat\xF3rio" }).max(max, { message: `Use at\xE9 ${String(max)} caracteres` });
var addressSchema = z8.strictObject({
  postalCode: postalCodeSchema,
  street: requiredText(ADDRESS_LIMITS.street),
  number: requiredText(ADDRESS_LIMITS.number),
  complement: requiredText(ADDRESS_LIMITS.complement).nullish(),
  district: requiredText(ADDRESS_LIMITS.district),
  city: requiredText(ADDRESS_LIMITS.city),
  state: brazilianStateSchema
});

// src/primitives/cnpj.ts
import { z as z9 } from "zod";
var CNPJ_FORMAT = /^[0-9A-Z]{12}[0-9]{2}$/;
var FIRST_CHECK_DIGIT_WEIGHTS = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
var SECOND_CHECK_DIGIT_WEIGHTS = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
var ASCII_ZERO = 48;
function characterValue(character) {
  return character.charCodeAt(0) - ASCII_ZERO;
}
function checkDigit(base, weights) {
  let sum = 0;
  for (let index = 0; index < weights.length; index += 1) {
    sum += characterValue(base.charAt(index)) * (weights[index] ?? 0);
  }
  const remainder = sum % 11;
  return remainder < 2 ? 0 : 11 - remainder;
}
function isValidCnpj(value) {
  if (!CNPJ_FORMAT.test(value)) return false;
  if (new Set(value).size === 1) return false;
  const first = checkDigit(value.slice(0, 12), FIRST_CHECK_DIGIT_WEIGHTS);
  const second = checkDigit(value.slice(0, 12) + String(first), SECOND_CHECK_DIGIT_WEIGHTS);
  return value.slice(12) === `${String(first)}${String(second)}`;
}
var cnpjSchema = z9.string().overwrite((value) => value.toUpperCase()).refine(isValidCnpj, { message: "CNPJ inv\xE1lido" }).meta({
  description: "CNPJ com 14 caracteres, sem m\xE1scara, num\xE9rico ou alfanum\xE9rico",
  example: "12ABC34501DE35"
});

// src/primitives/company-filter.ts
import { z as z10 } from "zod";
var MAX_FILTERED_COMPANIES = 200;
var companyIdsFilterSchema = z10.string().transform((value) => [
  ...new Set(
    value.split(",").map((item) => item.trim()).filter((item) => item !== "")
  )
]).pipe(z10.array(uuidSchema).min(1).max(MAX_FILTERED_COMPANIES)).meta({
  description: "Empresas do escopo selecionado, separadas por v\xEDrgula",
  example: "0199b4c8-6f1e-7c3a-9a51-3f2a1c0d9e10,0199b4c8-6f1e-7c3a-9a51-3f2a1c0d9e11"
});

// src/primitives/list-query.ts
import { z as z11 } from "zod";
var MAX_SEARCH_LENGTH = 100;
var searchTermSchema = z11.string().trim().min(1).max(MAX_SEARCH_LENGTH);
function listQuerySchema(sortBy) {
  return paginationQuerySchema.extend({
    search: searchTermSchema.optional(),
    sortBy,
    sortOrder: sortOrderSchema.default("asc")
  });
}

// src/company/company.ts
var legalNameSchema = z12.string().trim().min(1, { message: "Informe a raz\xE3o social" }).max(200, { message: "Use at\xE9 200 caracteres" });
var tradeNameSchema = z12.string().trim().min(1, { message: "Informe o nome fantasia ou deixe vazio" }).max(200, { message: "Use at\xE9 200 caracteres" });
var STATE_REGISTRATION_FORMAT = /^[0-9A-Z]{2,14}$/;
var MUNICIPAL_REGISTRATION_FORMAT = /^[0-9A-Z./-]{1,20}$/;
var ACTIVITY_CODE_FORMAT = /^\d{7}$/;
function isValidStateRegistration(value) {
  return STATE_REGISTRATION_FORMAT.test(value);
}
function isValidMunicipalRegistration(value) {
  return MUNICIPAL_REGISTRATION_FORMAT.test(value);
}
function isValidActivityCode(value) {
  return ACTIVITY_CODE_FORMAT.test(value);
}
var stateRegistrationSchema = z12.string().trim().overwrite((value) => value.toUpperCase()).refine(isValidStateRegistration, {
  message: "Inscri\xE7\xE3o estadual inv\xE1lida: use de 2 a 14 letras ou n\xFAmeros, sem pontua\xE7\xE3o"
}).meta({ description: "Inscri\xE7\xE3o estadual sem m\xE1scara, ou ISENTO", example: "110042490114" });
var municipalRegistrationSchema = z12.string().trim().overwrite((value) => value.toUpperCase()).refine(isValidMunicipalRegistration, {
  message: "Inscri\xE7\xE3o municipal inv\xE1lida: use at\xE9 20 letras, n\xFAmeros, ponto, barra ou h\xEDfen"
}).meta({ description: "Inscri\xE7\xE3o municipal", example: "1.234.567-8" });
var activityCodeSchema = z12.string().refine(isValidActivityCode, {
  message: "CNAE inv\xE1lido: informe os 7 d\xEDgitos da subclasse, sem m\xE1scara"
}).meta({ description: "CNAE da atividade principal, 7 d\xEDgitos sem m\xE1scara", example: "6201501" });
var COMPANY_LOGO_MAX_BYTES = 512 * 1024;
var COMPANY_LOGO_MAX_BASE64_LENGTH = Math.ceil(COMPANY_LOGO_MAX_BYTES / 3) * 4;
var companyLogoContentTypeSchema = z12.enum(["image/png", "image/jpeg", "image/webp"]);
var logoChecksumSchema = z12.string().regex(/^[0-9a-f]{64}$/);
var createCompanyRequestSchema = z12.strictObject({
  legalName: legalNameSchema,
  tradeName: tradeNameSchema.nullish(),
  cnpj: cnpjSchema,
  kind: companyKindSchema,
  headquartersId: uuidSchema.nullish(),
  stateRegistration: stateRegistrationSchema.nullish(),
  municipalRegistration: municipalRegistrationSchema.nullish(),
  taxRegime: taxRegimeSchema.nullish(),
  mainActivityCode: activityCodeSchema.nullish(),
  activityStartDate: isoDateSchema.nullish(),
  address: addressSchema.nullish()
}).superRefine((company, context) => {
  const hasHeadquarters = company.headquartersId !== void 0 && company.headquartersId !== null;
  if (company.kind === "BRANCH" && !hasHeadquarters) {
    context.addIssue({
      code: "custom",
      path: ["headquartersId"],
      message: "Filial deve informar a matriz"
    });
  }
  if (company.kind === "HEADQUARTERS" && hasHeadquarters) {
    context.addIssue({
      code: "custom",
      path: ["headquartersId"],
      message: "Matriz n\xE3o pode ter matriz"
    });
  }
});
var updateCompanyRequestSchema = z12.strictObject({
  legalName: legalNameSchema,
  tradeName: tradeNameSchema.nullable(),
  stateRegistration: stateRegistrationSchema.nullable(),
  municipalRegistration: municipalRegistrationSchema.nullable(),
  taxRegime: taxRegimeSchema.nullable(),
  mainActivityCode: activityCodeSchema.nullable(),
  activityStartDate: isoDateSchema.nullable(),
  address: addressSchema.nullable(),
  version: z12.number().int().min(1)
});
var companySchema = z12.object({
  id: uuidSchema,
  organizationId: uuidSchema,
  legalName: z12.string(),
  tradeName: z12.string().nullable(),
  cnpj: cnpjSchema,
  kind: companyKindSchema,
  headquartersId: uuidSchema.nullable(),
  status: recordStatusSchema,
  stateRegistration: z12.string().nullable(),
  municipalRegistration: z12.string().nullable(),
  taxRegime: taxRegimeSchema.nullable(),
  mainActivityCode: z12.string().nullable(),
  activityStartDate: isoDateSchema.nullable(),
  address: addressSchema.nullable(),
  logoChecksum: logoChecksumSchema.nullable(),
  version: z12.number().int().min(1),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema
});
var companyLogoUploadSchema = z12.strictObject({
  contentType: companyLogoContentTypeSchema,
  data: z12.base64().min(1).max(COMPANY_LOGO_MAX_BASE64_LENGTH, { message: "O logo deve ter at\xE9 512 KB" }).meta({ description: "Arquivo da imagem em base64" })
});
var companySortFieldSchema = z12.enum(["legalName", "tradeName", "cnpj", "createdAt"]);
var listCompaniesQuerySchema = listQuerySchema(
  companySortFieldSchema.default("legalName")
).extend({
  kind: companyKindSchema.optional(),
  status: recordStatusSchema.optional(),
  companyIds: companyIdsFilterSchema.optional()
});
var companyIdParamsSchema = z12.object({ companyId: uuidSchema });
var companyListResponseSchema = paginatedResponseSchema(companySchema);

// src/company/company-lookup.ts
import { z as z13 } from "zod";
var lookupAddressSchema = z13.object({
  postalCode: postalCodeSchema.nullable(),
  street: z13.string().nullable(),
  number: z13.string().nullable(),
  complement: z13.string().nullable(),
  district: z13.string().nullable(),
  city: z13.string().nullable(),
  state: brazilianStateSchema.nullable()
});
var cnpjLookupResponseSchema = z13.object({
  cnpj: cnpjSchema,
  legalName: z13.string(),
  tradeName: z13.string().nullable(),
  kind: companyKindSchema,
  registrationStatus: cnpjRegistrationStatusSchema.nullable(),
  mainActivityCode: activityCodeSchema.nullable(),
  mainActivityDescription: z13.string().nullable(),
  activityStartDate: isoDateSchema.nullable(),
  taxRegime: taxRegimeSchema.nullable(),
  address: lookupAddressSchema
});
var postalCodeLookupResponseSchema = z13.object({
  postalCode: postalCodeSchema,
  street: z13.string().nullable(),
  complement: z13.string().nullable(),
  district: z13.string().nullable(),
  city: z13.string(),
  state: brazilianStateSchema
});
var cnpjLookupParamsSchema = z13.object({ cnpj: cnpjSchema });
var postalCodeLookupParamsSchema = z13.object({ postalCode: postalCodeSchema });

// src/finance/accounting-period.ts
import { z as z14 } from "zod";
var PERIOD_REOPEN_JUSTIFICATION_MIN = 10;
var PERIOD_REOPEN_JUSTIFICATION_MAX = 500;
var ACCOUNTING_PERIOD_CLOSED_CODE = "ACCOUNTING_PERIOD_CLOSED";
function isMonthClosed(closedThrough, month) {
  return closedThrough !== null && month <= closedThrough;
}
var accountingPeriodClosingSchema = z14.object({
  companyId: uuidSchema,
  closedThrough: yearMonthSchema.nullable(),
  version: z14.number().int().min(0),
  updatedAt: isoDateTimeSchema.nullable(),
  canClose: z14.boolean(),
  canReopen: z14.boolean()
});
var accountingPeriodListQuerySchema = z14.object({
  companyIds: companyIdsFilterSchema.optional()
});
var accountingPeriodListSchema = z14.object({
  items: z14.array(accountingPeriodClosingSchema)
});
var closeAccountingPeriodRequestSchema = z14.strictObject({
  companyId: uuidSchema,
  month: yearMonthSchema,
  version: z14.number().int().min(0)
});
var reopenAccountingPeriodRequestSchema = z14.strictObject({
  companyId: uuidSchema,
  month: yearMonthSchema,
  justification: z14.string().trim().min(
    PERIOD_REOPEN_JUSTIFICATION_MIN,
    `Explique o motivo em ao menos ${String(PERIOD_REOPEN_JUSTIFICATION_MIN)} caracteres`
  ).max(PERIOD_REOPEN_JUSTIFICATION_MAX),
  version: z14.number().int().min(1)
});
var accountingPeriodCompanyParamsSchema = z14.object({ companyId: uuidSchema });

// src/finance/bank-account.ts
import { z as z16 } from "zod";

// src/primitives/money.ts
import { z as z15 } from "zod";
var MONEY_MAX_INTEGER_DIGITS = 16;
var MONEY_SCALE = 2;
var MONEY_FORMAT = /^(-?)(\d+)(?:\.(\d{1,2}))?$/;
function toCanonicalMoney(value) {
  const match = MONEY_FORMAT.exec(value);
  if (!match) return null;
  const [, sign = "", rawInteger = "", rawFraction = ""] = match;
  const integer = rawInteger.replace(/^0+(?=\d)/, "");
  if (integer.length > MONEY_MAX_INTEGER_DIGITS) return null;
  const fraction = rawFraction.padEnd(MONEY_SCALE, "0");
  const isZero = /^0+$/.test(integer) && /^0+$/.test(fraction);
  return `${isZero ? "" : sign}${integer}.${fraction}`;
}
var moneySchema = z15.string().refine((value) => toCanonicalMoney(value) !== null, {
  message: "Valor monet\xE1rio inv\xE1lido: use at\xE9 16 d\xEDgitos inteiros e 2 decimais, com ponto",
  abort: true
}).overwrite((value) => toCanonicalMoney(value) ?? value).meta({
  description: "Valor decimal em string, compat\xEDvel com NUMERIC(18,2)",
  example: "1234.50"
});

// src/finance/bank-account.ts
var versionSchema = z16.number().int().min(1);
var descriptionSchema = z16.string().trim().min(1).max(200);
var bankCodeSchema = z16.string().regex(/^\d{3}$/, { message: "C\xF3digo do banco: 3 d\xEDgitos" }).meta({ description: "C\xF3digo COMPE do banco", example: "341" });
var branchNumberSchema = z16.string().trim().regex(/^\d{1,10}$/, { message: "Ag\xEAncia: s\xF3 d\xEDgitos" });
var accountNumberSchema = z16.string().trim().regex(/^\d{1,20}$/, { message: "Conta: s\xF3 d\xEDgitos" });
var accountCheckDigitSchema = z16.string().trim().toUpperCase().regex(/^[0-9A-Z]{1,2}$/, { message: "D\xEDgito: 1 ou 2 letras ou n\xFAmeros" });
var bankAccountSchema = z16.object({
  id: uuidSchema,
  organizationId: uuidSchema,
  companyId: uuidSchema,
  kind: bankAccountKindSchema,
  description: z16.string(),
  bankCode: z16.string().nullable(),
  branchNumber: z16.string().nullable(),
  accountNumber: z16.string().nullable(),
  accountCheckDigit: z16.string().nullable(),
  ledgerAccountId: uuidSchema,
  initialBalance: moneySchema,
  initialBalanceDate: isoDateSchema,
  currentBalance: moneySchema,
  status: recordStatusSchema,
  version: versionSchema
});
var createBankAccountRequestSchema = z16.strictObject({
  companyId: uuidSchema,
  kind: bankAccountKindSchema,
  description: descriptionSchema,
  bankCode: bankCodeSchema.nullish(),
  branchNumber: branchNumberSchema.nullish(),
  accountNumber: accountNumberSchema.nullish(),
  accountCheckDigit: accountCheckDigitSchema.nullish(),
  ledgerAccountId: uuidSchema,
  initialBalance: moneySchema,
  initialBalanceDate: isoDateSchema
});
var updateBankAccountRequestSchema = z16.strictObject({
  version: versionSchema,
  kind: bankAccountKindSchema.optional(),
  description: descriptionSchema.optional(),
  bankCode: bankCodeSchema.nullable().optional(),
  branchNumber: branchNumberSchema.nullable().optional(),
  accountNumber: accountNumberSchema.nullable().optional(),
  accountCheckDigit: accountCheckDigitSchema.nullable().optional(),
  ledgerAccountId: uuidSchema.optional(),
  initialBalance: moneySchema.optional(),
  initialBalanceDate: isoDateSchema.optional(),
  status: recordStatusSchema.optional()
});
var bankAccountIdParamsSchema = z16.object({ bankAccountId: uuidSchema });
var bankAccountSortFieldSchema = z16.enum(["description", "bank", "currentBalance"]);
var listBankAccountsQuerySchema = listQuerySchema(
  bankAccountSortFieldSchema.default("description")
).extend({
  kind: bankAccountKindSchema.optional(),
  status: recordStatusSchema.optional(),
  companyIds: companyIdsFilterSchema.optional()
});
var companyBalanceTotalSchema = z16.object({
  companyId: uuidSchema,
  currentBalance: moneySchema,
  accountCount: z16.number().int().min(1)
});
var bankAccountListResponseSchema = paginatedResponseSchema(bankAccountSchema).extend({
  balanceDate: isoDateSchema,
  totals: z16.object({
    overall: moneySchema,
    byCompany: z16.array(companyBalanceTotalSchema)
  })
});

// src/finance/bank-statement.ts
import { z as z17 } from "zod";
var BANK_STATEMENT_MAX_TRANSACTIONS = 2e3;
var RECONCILIATION_WINDOW_DAYS = 3;
var MAX_RECONCILIATION_SUGGESTIONS = 5;
var signedAmountSchema = moneySchema.refine((value) => value !== "0.00", {
  message: "O valor da transa\xE7\xE3o n\xE3o pode ser zero"
});
var bankStatementTransactionSchema = z17.strictObject({
  line: z17.number().int().min(1),
  fitId: z17.string().trim().min(1).max(255),
  postedOn: isoDateSchema,
  amount: signedAmountSchema,
  type: z17.string().trim().min(1).max(20).nullish(),
  description: z17.string().trim().min(1).max(500).nullish(),
  checkNumber: z17.string().trim().min(1).max(40).nullish()
});
var bankStatementImportRequestSchema = z17.strictObject({
  bankAccountId: uuidSchema,
  fileName: z17.string().trim().min(1).max(255),
  fileSha256: z17.string().regex(/^[0-9a-f]{64}$/, { message: "SHA-256 em hexadecimal" }),
  periodStart: isoDateSchema,
  periodEnd: isoDateSchema,
  ledgerBalance: moneySchema.nullish(),
  transactions: z17.array(bankStatementTransactionSchema).min(1).max(BANK_STATEMENT_MAX_TRANSACTIONS)
}).superRefine((request, context) => {
  if (request.periodEnd < request.periodStart) {
    context.addIssue({
      code: "custom",
      path: ["periodEnd"],
      message: "O fim do per\xEDodo n\xE3o pode ser anterior ao in\xEDcio"
    });
  }
  const seen = /* @__PURE__ */ new Set();
  request.transactions.forEach((transaction, index) => {
    if (seen.has(transaction.line)) {
      context.addIssue({
        code: "custom",
        path: ["transactions", index, "line"],
        message: "Linha repetida no envio"
      });
    }
    seen.add(transaction.line);
  });
});
var bankStatementTransactionStatusSchema = z17.enum(["NEW", "DUPLICATE"]);
var bankStatementDuplicateReasonSchema = z17.enum(["ALREADY_IMPORTED", "REPEATED_IN_FILE"]);
var bankStatementTransactionResultSchema = z17.object({
  line: z17.number().int(),
  status: bankStatementTransactionStatusSchema,
  duplicateReason: bankStatementDuplicateReasonSchema.nullable(),
  duplicateOfLine: z17.number().int().nullable()
});
var bankStatementFlowSchema = z17.object({ count: z17.number().int(), amount: moneySchema });
var bankStatementPreviewResponseSchema = z17.object({
  transactions: z17.array(bankStatementTransactionResultSchema),
  totals: z17.object({ new: z17.number().int(), duplicate: z17.number().int() }),
  credits: bankStatementFlowSchema,
  debits: bankStatementFlowSchema,
  calculatedBalance: z17.object({ amount: moneySchema, asOf: isoDateSchema })
});
var bankStatementImportResultSchema = z17.object({
  statementId: uuidSchema.nullable(),
  imported: z17.number().int(),
  skipped: z17.array(bankStatementTransactionResultSchema)
});
var reconciliationTitleSchema = z17.object({
  id: uuidSchema,
  kind: titleKindSchema,
  description: z17.string(),
  documentNumber: z17.string().nullable(),
  partyName: z17.string(),
  amount: moneySchema,
  dueDate: isoDateSchema,
  installment: z17.object({ number: z17.number().int(), count: z17.number().int() }).nullable()
});
var reconciliationSuggestionSchema = reconciliationTitleSchema.extend({
  daysApart: z17.number().int().min(0)
});
var bankStatementLineSchema = z17.object({
  id: uuidSchema,
  bankAccountId: uuidSchema,
  companyId: uuidSchema,
  statementId: uuidSchema,
  fitId: z17.string(),
  postedOn: isoDateSchema,
  amount: moneySchema,
  type: z17.string().nullable(),
  description: z17.string().nullable(),
  status: bankStatementLineStatusSchema,
  title: reconciliationTitleSchema.nullable(),
  ignoredReason: z17.string().nullable(),
  resolvedByUserId: uuidSchema.nullable(),
  resolvedAt: isoDateTimeSchema.nullable(),
  /** Mês da transação fechado na empresa da conta (ADR-0025): conciliar e desfazer ficam bloqueados. */
  periodClosed: z17.boolean(),
  version: z17.number().int().min(1),
  suggestions: z17.array(reconciliationSuggestionSchema),
  suggestionCount: z17.number().int().min(0)
});
var listBankStatementLinesQuerySchema = paginationQuerySchema.extend({
  bankAccountId: uuidSchema,
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  status: bankStatementLineStatusSchema.optional()
});
var bankStatementLineListResponseSchema = paginatedResponseSchema(bankStatementLineSchema);
var bankStatementLineIdParamsSchema = z17.object({ lineId: uuidSchema });
var reconciliationCandidatesQuerySchema = z17.object({
  search: searchTermSchema.optional()
});
var reconciliationCandidatesResponseSchema = z17.object({
  items: z17.array(reconciliationTitleSchema)
});
var versionSchema2 = z17.number().int().min(1);
var reconcileBankStatementLineRequestSchema = z17.strictObject({
  titleId: uuidSchema,
  version: versionSchema2
});
var ignoreBankStatementLineRequestSchema = z17.strictObject({
  reason: z17.string().trim().min(1).max(200).nullish(),
  version: versionSchema2
});
var bankStatementLineVersionRequestSchema = z17.strictObject({ version: versionSchema2 });
var bankStatementLineVersionQuerySchema = z17.object({
  version: z17.coerce.number().int().min(1)
});

// src/finance/brazilian-banks.ts
var BRAZILIAN_BANKS = [
  { code: "001", name: "Banco do Brasil" },
  { code: "003", name: "Banco da Amaz\xF4nia" },
  { code: "004", name: "Banco do Nordeste" },
  { code: "021", name: "Banestes" },
  { code: "025", name: "Banco Alfa" },
  { code: "033", name: "Santander" },
  { code: "037", name: "Banpar\xE1" },
  { code: "041", name: "Banrisul" },
  { code: "047", name: "Banese" },
  { code: "069", name: "Banco Crefisa" },
  { code: "070", name: "BRB - Banco de Bras\xEDlia" },
  { code: "074", name: "Banco J. Safra" },
  { code: "077", name: "Banco Inter" },
  { code: "085", name: "Cooperativa Central Ailos" },
  { code: "104", name: "Caixa Econ\xF4mica Federal" },
  { code: "121", name: "Banco Agibank" },
  { code: "133", name: "Cresol" },
  { code: "136", name: "Unicred" },
  { code: "197", name: "Stone" },
  { code: "208", name: "BTG Pactual" },
  { code: "212", name: "Banco Original" },
  { code: "218", name: "Banco BS2" },
  { code: "224", name: "Banco Fibra" },
  { code: "237", name: "Bradesco" },
  { code: "246", name: "Banco ABC Brasil" },
  { code: "260", name: "Nubank" },
  { code: "290", name: "PagBank" },
  { code: "318", name: "Banco BMG" },
  { code: "323", name: "Mercado Pago" },
  { code: "336", name: "Banco C6" },
  { code: "341", name: "Ita\xFA Unibanco" },
  { code: "364", name: "Ef\xED" },
  { code: "380", name: "PicPay" },
  { code: "389", name: "Banco Mercantil do Brasil" },
  { code: "403", name: "Cora" },
  { code: "422", name: "Banco Safra" },
  { code: "536", name: "Neon" },
  { code: "604", name: "Banco Industrial do Brasil" },
  { code: "623", name: "Banco Pan" },
  { code: "633", name: "Banco Rendimento" },
  { code: "643", name: "Banco Pine" },
  { code: "655", name: "Banco BV" },
  { code: "707", name: "Banco Daycoval" },
  { code: "745", name: "Citibank" },
  { code: "748", name: "Sicredi" },
  { code: "756", name: "Sicoob" }
];
var BANK_NAMES = new Map(BRAZILIAN_BANKS.map((bank) => [bank.code, bank.name]));
function brazilianBankName(code) {
  return BANK_NAMES.get(code) ?? null;
}

// src/finance/catalog.ts
import { z as z18 } from "zod";

// src/finance/chart-of-accounts.ts
var MAX_LEDGER_ACCOUNT_LEVEL = 4;
var BALANCE_SHEET_GROUP_CATEGORIES = {
  CURRENT_ASSETS: "ASSET",
  LONG_TERM_ASSETS: "ASSET",
  INVESTMENTS: "ASSET",
  PROPERTY_PLANT_AND_EQUIPMENT: "ASSET",
  INTANGIBLE_ASSETS: "ASSET",
  CURRENT_LIABILITIES: "LIABILITY",
  NON_CURRENT_LIABILITIES: "LIABILITY",
  SHARE_CAPITAL: "EQUITY",
  CAPITAL_RESERVES: "EQUITY",
  VALUATION_ADJUSTMENTS: "EQUITY",
  PROFIT_RESERVES: "EQUITY",
  TREASURY_SHARES: "EQUITY",
  ACCUMULATED_PROFIT_OR_LOSS: "EQUITY"
};
var BALANCE_SHEET_GROUPS = Object.keys(BALANCE_SHEET_GROUP_CATEGORIES);
function groupsOfCategory(category) {
  return BALANCE_SHEET_GROUPS.filter((group) => BALANCE_SHEET_GROUP_CATEGORIES[group] === category);
}
function resultRule(category, usualNature, incomeStatementLines) {
  return { category, usualNature, incomeStatementLines, balanceSheetGroups: [] };
}
function balanceSheetRule(category, usualNature) {
  return {
    category,
    usualNature,
    incomeStatementLines: [],
    balanceSheetGroups: groupsOfCategory(category)
  };
}
var LEDGER_ACCOUNT_TYPE_RULES = {
  REVENUE: resultRule("REVENUE", "CREDIT", ["GROSS_REVENUE"]),
  DEDUCTION: resultRule("REVENUE", "DEBIT", ["REVENUE_DEDUCTIONS"]),
  FINANCIAL_REVENUE: resultRule("REVENUE", "CREDIT", ["FINANCIAL_RESULT"]),
  COST: resultRule("EXPENSE", "DEBIT", ["COSTS"]),
  EXPENSE: resultRule("EXPENSE", "DEBIT", ["OPERATING_EXPENSES", "INCOME_TAXES"]),
  FINANCIAL_EXPENSE: resultRule("EXPENSE", "DEBIT", ["FINANCIAL_RESULT"]),
  ASSET: balanceSheetRule("ASSET", "DEBIT"),
  LIABILITY: balanceSheetRule("LIABILITY", "CREDIT"),
  EQUITY: balanceSheetRule("EQUITY", "CREDIT")
};
var ACCOUNT_TYPES = Object.keys(LEDGER_ACCOUNT_TYPE_RULES);
function financialStatementOf(category) {
  return category === "REVENUE" || category === "EXPENSE" ? "INCOME_STATEMENT" : "BALANCE_SHEET";
}
function accountTypesOf(category) {
  return ACCOUNT_TYPES.filter((type) => LEDGER_ACCOUNT_TYPE_RULES[type].category === category);
}
function incomeStatementLinesOf(category) {
  return [
    ...new Set(
      accountTypesOf(category).flatMap(
        (type) => LEDGER_ACCOUNT_TYPE_RULES[type].incomeStatementLines
      )
    )
  ];
}
function isBalanceSheetCategory(category) {
  return financialStatementOf(category) === "BALANCE_SHEET";
}
function balanceSheetGroupsOf(category) {
  return isBalanceSheetCategory(category) ? [...groupsOfCategory(category)] : [];
}
function accountTypeViolation(classification) {
  const { kind, accountType, category } = classification;
  if (kind === "ANALYTIC" && accountType === null) {
    return {
      field: "accountType",
      code: "LEDGER_ACCOUNT_TYPE_REQUIRED",
      message: "Informe a natureza da conta anal\xEDtica"
    };
  }
  if (kind === "SYNTHETIC" && accountType !== null) {
    return {
      field: "accountType",
      code: "LEDGER_ACCOUNT_TYPE_NOT_ALLOWED",
      message: "Conta sint\xE9tica (grupo) n\xE3o tem natureza; ela vale s\xF3 para a anal\xEDtica"
    };
  }
  if (accountType !== null && LEDGER_ACCOUNT_TYPE_RULES[accountType].category !== category) {
    return {
      field: "accountType",
      code: "LEDGER_ACCOUNT_TYPE_CATEGORY_MISMATCH",
      message: "A natureza n\xE3o combina com a categoria da conta"
    };
  }
  return null;
}
function incomeStatementLineViolation(classification) {
  const { incomeStatementLine, accountType, category } = classification;
  if (incomeStatementLine === null) return null;
  const allowed = accountType === null ? incomeStatementLinesOf(category) : LEDGER_ACCOUNT_TYPE_RULES[accountType].incomeStatementLines;
  if (allowed.includes(incomeStatementLine)) return null;
  return {
    field: "incomeStatementLine",
    code: "LEDGER_ACCOUNT_INCOME_STATEMENT_LINE_NOT_ALLOWED",
    message: financialStatementOf(category) === "INCOME_STATEMENT" ? "A linha da DRE n\xE3o combina com a natureza da conta" : "Somente conta de receita ou despesa tem linha na DRE"
  };
}
function balanceSheetGroupViolation(classification) {
  const { balanceSheetGroup, accountType, category } = classification;
  if (balanceSheetGroup === null) return null;
  const allowed = accountType === null ? balanceSheetGroupsOf(category) : LEDGER_ACCOUNT_TYPE_RULES[accountType].balanceSheetGroups;
  if (allowed.includes(balanceSheetGroup)) return null;
  return {
    field: "balanceSheetGroup",
    code: "LEDGER_ACCOUNT_BALANCE_SHEET_GROUP_NOT_ALLOWED",
    message: isBalanceSheetCategory(category) ? "O grupo do Balan\xE7o n\xE3o combina com a categoria da conta" : "Somente conta de ativo, passivo ou patrim\xF4nio l\xEDquido tem grupo no Balan\xE7o"
  };
}
function ledgerAccountClassificationViolations(classification) {
  return [
    accountTypeViolation(classification),
    incomeStatementLineViolation(classification),
    balanceSheetGroupViolation(classification)
  ].filter((violation) => violation !== null);
}
var CHART_TEMPLATE_TAX_REGIMES = {
  PME_SIMPLIFIED: "SIMPLIFIED",
  PME_PRESUMED_PROFIT: "PRESUMED_PROFIT",
  PME_ACTUAL_PROFIT: "ACTUAL_PROFIT"
};

// src/finance/catalog.ts
var LEDGER_ACCOUNT_CODE_FORMAT = /^\d+(\.\d+)*$/;
var ledgerAccountCodeSchema = z18.string().trim().max(50).regex(LEDGER_ACCOUNT_CODE_FORMAT, {
  message: "C\xF3digo da conta: n\xFAmeros separados por ponto (ex.: 4.1.01.001)"
}).meta({ description: "C\xF3digo da conta do plano", example: "4.1.01.001" });
var catalogNameSchema = z18.string().trim().min(1).max(200);
var costCenterCodeSchema = z18.string().trim().min(1).max(50);
var versionSchema3 = z18.number().int().min(1);
var ledgerAccountMappingSchema = z18.object({
  incomeStatementLine: incomeStatementLineSchema.nullable(),
  balanceSheetGroup: balanceSheetGroupSchema.nullable(),
  inheritedFromId: uuidSchema.nullable(),
  issue: ledgerAccountMappingIssueSchema.nullable()
});
var ledgerAccountSchema = z18.object({
  id: uuidSchema,
  organizationId: uuidSchema,
  code: z18.string(),
  name: z18.string(),
  category: ledgerAccountCategorySchema,
  nature: ledgerNatureSchema,
  kind: ledgerAccountKindSchema,
  /** Natureza da analítica; `null` em grupo ou em conta antiga ainda sem natureza. */
  accountType: ledgerAccountTypeSchema.nullable(),
  parentId: uuidSchema.nullable(),
  /** Linha da DRE da própria classificação; `null` herda a da classificação pai. */
  incomeStatementLine: incomeStatementLineSchema.nullable(),
  /** Grupo do Balanço da própria classificação; `null` herda o da classificação pai. */
  balanceSheetGroup: balanceSheetGroupSchema.nullable(),
  mapping: ledgerAccountMappingSchema,
  status: recordStatusSchema,
  version: versionSchema3
});
function refineClassification(request, context) {
  const violations = ledgerAccountClassificationViolations({
    category: request.category,
    kind: request.kind,
    accountType: request.accountType ?? null,
    incomeStatementLine: request.incomeStatementLine ?? null,
    balanceSheetGroup: request.balanceSheetGroup ?? null
  });
  for (const violation of violations) {
    context.addIssue({ code: "custom", path: [violation.field], message: violation.message });
  }
}
var createLedgerAccountRequestSchema = z18.strictObject({
  code: ledgerAccountCodeSchema,
  name: catalogNameSchema,
  category: ledgerAccountCategorySchema,
  nature: ledgerNatureSchema,
  kind: ledgerAccountKindSchema,
  parentId: uuidSchema.nullish(),
  accountType: ledgerAccountTypeSchema.nullish(),
  incomeStatementLine: incomeStatementLineSchema.nullish(),
  balanceSheetGroup: balanceSheetGroupSchema.nullish()
}).superRefine(refineClassification);
var updateLedgerAccountRequestSchema = z18.strictObject({
  version: versionSchema3,
  code: ledgerAccountCodeSchema.optional(),
  name: catalogNameSchema.optional(),
  parentId: uuidSchema.nullable().optional(),
  kind: ledgerAccountKindSchema.optional(),
  accountType: ledgerAccountTypeSchema.optional(),
  incomeStatementLine: incomeStatementLineSchema.nullable().optional(),
  balanceSheetGroup: balanceSheetGroupSchema.nullable().optional(),
  status: recordStatusSchema.optional()
});
var ledgerAccountIdParamsSchema = z18.object({ ledgerAccountId: uuidSchema });
var ledgerAccountSortFieldSchema = z18.enum(["code", "name"]);
var listLedgerAccountsQuerySchema = listQuerySchema(
  ledgerAccountSortFieldSchema.default("code")
).extend({
  kind: ledgerAccountKindSchema.optional(),
  category: ledgerAccountCategorySchema.optional(),
  status: recordStatusSchema.optional()
});
var ledgerAccountListResponseSchema = paginatedResponseSchema(ledgerAccountSchema);
var initializeChartOfAccountsRequestSchema = z18.strictObject({
  template: chartTemplateCodeSchema
});
var appliedChartTemplateSchema = z18.object({
  code: chartTemplateCodeSchema,
  version: z18.number().int().min(1),
  initializedAt: isoDateTimeSchema
});
var initializeChartOfAccountsResponseSchema = z18.object({
  outcome: z18.enum(["INITIALIZED", "ALREADY_INITIALIZED"]),
  template: appliedChartTemplateSchema,
  createdAccounts: z18.number().int().min(0)
});
var costCenterCompanyIdSchema = uuidSchema.nullable().meta({
  description: "Empresa exclusiva do centro de custo; nulo = compartilhado pelo grupo"
});
var costCenterSchema = z18.object({
  id: uuidSchema,
  organizationId: uuidSchema,
  code: z18.string(),
  name: z18.string(),
  companyId: costCenterCompanyIdSchema,
  parentId: uuidSchema.nullable(),
  status: recordStatusSchema,
  version: versionSchema3
});
var createCostCenterRequestSchema = z18.strictObject({
  code: costCenterCodeSchema,
  name: catalogNameSchema,
  parentId: uuidSchema.nullish(),
  companyId: costCenterCompanyIdSchema.optional()
});
var updateCostCenterRequestSchema = z18.strictObject({
  version: versionSchema3,
  code: costCenterCodeSchema.optional(),
  name: catalogNameSchema.optional(),
  parentId: uuidSchema.nullable().optional(),
  companyId: costCenterCompanyIdSchema.optional(),
  status: recordStatusSchema.optional()
});
var costCenterIdParamsSchema = z18.object({ costCenterId: uuidSchema });
var costCenterSortFieldSchema = z18.enum(["code", "name"]);
var listCostCentersQuerySchema = listQuerySchema(
  costCenterSortFieldSchema.default("code")
).extend({
  status: recordStatusSchema.optional(),
  companyId: uuidSchema.optional().meta({ description: "S\xF3 os centros de custo que servem a esta empresa" })
});
var costCenterListResponseSchema = paginatedResponseSchema(costCenterSchema);

// src/finance/finance-settings.ts
import { z as z19 } from "zod";
var financeSettingsVersionSchema = z19.number().int().min(0);
var financeSettingsSchema = z19.object({
  organizationId: uuidSchema,
  costCenterRequired: z19.boolean().meta({ description: "Todo lan\xE7amento (e cada linha do rateio) exige centro de custo" }),
  /** Plano padrão carregado na organização; `null` = plano montado à mão ou anterior. */
  chartTemplate: appliedChartTemplateSchema.nullable(),
  version: financeSettingsVersionSchema
});
var updateFinanceSettingsRequestSchema = z19.strictObject({
  version: financeSettingsVersionSchema,
  costCenterRequired: z19.boolean().optional()
});

// src/finance/import-template.ts
import { z as z20 } from "zod";
var titleImportFieldSchema = z20.enum([
  "kind",
  "companyCnpj",
  "partyDocument",
  "partyName",
  "ledgerAccountCode",
  "costCenterCode",
  "description",
  "documentNumber",
  "amount",
  "issueDate",
  "competenceDate",
  "dueDate",
  "notes"
]);
var importFieldSourceSchema = z20.discriminatedUnion("source", [
  z20.strictObject({ source: z20.literal("COLUMN"), column: z20.string().trim().min(1).max(200) }),
  z20.strictObject({ source: z20.literal("FIXED"), value: z20.string().trim().min(1).max(200) })
]);
var csvDelimiterSchema = z20.enum(["AUTO", "SEMICOLON", "COMMA", "TAB"]);
var importDateFormatSchema = z20.enum(["DD/MM/YYYY", "YYYY-MM-DD", "DD-MM-YYYY"]);
var decimalSeparatorSchema = z20.enum(["COMMA", "DOT"]);
var IMPORT_MAX_HEADER_ROW = 50;
var importTemplateSettingsSchema = z20.strictObject({
  delimiter: csvDelimiterSchema,
  headerRow: z20.number().int().min(1).max(IMPORT_MAX_HEADER_ROW),
  dateFormat: importDateFormatSchema,
  decimalSeparator: decimalSeparatorSchema,
  signDefinesKind: z20.boolean()
});
var importTemplateMappingSchema = z20.partialRecord(
  titleImportFieldSchema,
  importFieldSourceSchema
);
var templateNameSchema = z20.string().trim().min(1).max(80);
var createImportTemplateRequestSchema = z20.strictObject({
  kind: importTemplateKindSchema,
  name: templateNameSchema,
  settings: importTemplateSettingsSchema,
  mapping: importTemplateMappingSchema
});
var updateImportTemplateRequestSchema = z20.strictObject({
  name: templateNameSchema,
  settings: importTemplateSettingsSchema,
  mapping: importTemplateMappingSchema,
  version: z20.number().int().min(1)
});
var importTemplateIdParamsSchema = z20.object({ templateId: uuidSchema });
var listImportTemplatesQuerySchema = z20.object({
  kind: importTemplateKindSchema.default("TITLES")
});
var deleteImportTemplateQuerySchema = z20.object({
  version: z20.coerce.number().int().min(1)
});
var importTemplateSchema = z20.object({
  id: uuidSchema,
  kind: importTemplateKindSchema,
  name: z20.string(),
  settings: importTemplateSettingsSchema,
  mapping: importTemplateMappingSchema,
  version: z20.number().int().min(1),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema
});
var importTemplateListResponseSchema = z20.object({ items: z20.array(importTemplateSchema) });

// src/finance/income-statement.ts
import { z as z22 } from "zod";

// src/primitives/report-scope.ts
import { z as z21 } from "zod";
var SCOPE_PATTERN = /^(group|custom|company:(?<companyId>.+)|headquarters:(?<headquartersId>.+))$/;
var reportScopeSchema = z21.string().transform((value, context) => {
  const match = SCOPE_PATTERN.exec(value);
  const { companyId, headquartersId } = match?.groups ?? {};
  const id = companyId ?? headquartersId;
  if (match === null || id !== void 0 && !z21.uuid().safeParse(id).success) {
    context.addIssue({
      code: "custom",
      message: "Escopo inv\xE1lido; use group, custom, company:<id> ou headquarters:<id>"
    });
    return z21.NEVER;
  }
  if (companyId !== void 0) return { kind: "COMPANY", companyId };
  if (headquartersId !== void 0) return { kind: "HEADQUARTERS", headquartersId };
  return value === "group" ? { kind: "GROUP" } : { kind: "CUSTOM" };
}).meta({
  description: "Escopo do relat\xF3rio: group, custom, company:<id> ou headquarters:<id>",
  example: "headquarters:0199b4c8-6f1e-7c3a-9a51-3f2a1c0d9e10"
});
var reportScopeQueryShape = {
  scope: reportScopeSchema.optional(),
  companyIds: companyIdsFilterSchema.optional()
};
function refineReportScope(query, context) {
  if (query.scope?.kind === "CUSTOM" && query.companyIds === void 0) {
    context.addIssue({
      code: "custom",
      path: ["companyIds"],
      message: "Informe as empresas da sele\xE7\xE3o personalizada"
    });
  } else if (query.scope !== void 0 && query.scope.kind !== "CUSTOM" && query.companyIds !== void 0) {
    context.addIssue({
      code: "custom",
      path: ["companyIds"],
      message: "As empresas s\xF3 podem ser escolhidas na sele\xE7\xE3o personalizada (scope=custom)"
    });
  }
}

// src/finance/income-statement.ts
var MAX_INCOME_STATEMENT_ENTRIES_MONTHS = 24;
var incomeStatementTotalSchema = z22.enum([
  "NET_REVENUE",
  "GROSS_PROFIT",
  "OPERATING_RESULT",
  "PROFIT_BEFORE_TAXES",
  "NET_PROFIT"
]);
var incomeStatementRowCodeSchema = z22.enum([
  ...incomeStatementLineSchema.options,
  "UNCLASSIFIED",
  ...incomeStatementTotalSchema.options
]);
var signedPercentSchema = z22.string().regex(/^-?\d+\.\d{2}$/).meta({ description: "Percentual decimal em string, com sinal e duas casas", example: "-12.50" });
var incomeStatementFiltersSchema = z22.object({
  basis: accountingBasisSchema.default("ACCRUAL"),
  period: statementPeriodSchema.default("MONTH"),
  /** Mês de referência: o próprio mês, o do trimestre/ano ou o fim do acumulado do ano. */
  reference: yearMonthSchema,
  /**
   * Mês de referência do período com que o total é comparado, do mesmo tipo de `period`. Igual
   * ao período da DRE responde 422.
   */
  compareReference: yearMonthSchema.optional()
});
var incomeStatementQuerySchema = incomeStatementFiltersSchema.extend(reportScopeQueryShape).superRefine(refineReportScope);
var incomeStatementComparisonSchema = z22.object({
  base: moneySchema,
  /** `(atual − base) / |base| × 100`; `null` quando a base é zero. */
  variationPercent: signedPercentSchema.nullable()
});
var incomeStatementCellSchema = z22.object({
  month: yearMonthSchema,
  amount: moneySchema,
  verticalPercent: signedPercentSchema.nullable(),
  previousMonth: incomeStatementComparisonSchema,
  previousYear: incomeStatementComparisonSchema
});
var incomeStatementTotalCellSchema = z22.object({
  amount: moneySchema,
  verticalPercent: signedPercentSchema.nullable(),
  previousYear: incomeStatementComparisonSchema,
  compared: incomeStatementComparisonSchema.nullable()
});
var incomeStatementAccountKindSchema = z22.enum(["GROUP", "CLASSIFICATION"]);
var incomeStatementAccountSchema = z22.object({
  ledgerAccountId: uuidSchema,
  code: z22.string(),
  name: z22.string(),
  kind: incomeStatementAccountKindSchema,
  cells: z22.array(incomeStatementCellSchema),
  total: incomeStatementTotalCellSchema,
  get children() {
    return z22.array(incomeStatementAccountSchema);
  }
}).meta({ id: "IncomeStatementAccount" });
var incomeStatementRowSchema = z22.object({
  code: incomeStatementRowCodeSchema,
  kind: z22.enum(["LINE", "TOTAL"]),
  cells: z22.array(incomeStatementCellSchema),
  total: incomeStatementTotalCellSchema,
  accounts: z22.array(incomeStatementAccountSchema)
});
var monthClosingStatusSchema = z22.enum(["OPEN", "PARTIAL", "CLOSED"]);
var incomeStatementMonthClosingSchema = z22.object({
  month: yearMonthSchema,
  status: monthClosingStatusSchema,
  closedCompanies: z22.number().int().min(0),
  totalCompanies: z22.number().int().min(0)
});
var incomeStatementSchema = z22.object({
  basis: accountingBasisSchema,
  period: statementPeriodSchema,
  reference: yearMonthSchema,
  months: z22.array(yearMonthSchema),
  /** Período de comparação do total; `null` sem `compareReference`. */
  comparison: z22.object({ reference: yearMonthSchema, months: z22.array(yearMonthSchema) }).nullable(),
  currency: z22.string().length(3),
  rows: z22.array(incomeStatementRowSchema),
  /** Fechamento de cada mês do período nas empresas do recorte (ADR-0025). */
  monthClosings: z22.array(incomeStatementMonthClosingSchema)
});
var incomeStatementEntriesQuerySchema = paginationQuerySchema.extend({
  basis: accountingBasisSchema.default("ACCRUAL"),
  ledgerAccountId: uuidSchema,
  from: yearMonthSchema,
  to: yearMonthSchema,
  ...reportScopeQueryShape
}).superRefine(refineReportScope).superRefine((query, context) => {
  const span = monthSpan(query.from, query.to);
  if (span < 1) {
    context.addIssue({
      code: "custom",
      path: ["to"],
      message: "O fim do per\xEDodo n\xE3o pode ser anterior ao in\xEDcio"
    });
  } else if (span > MAX_INCOME_STATEMENT_ENTRIES_MONTHS) {
    context.addIssue({
      code: "custom",
      path: ["to"],
      message: `O per\xEDodo pode ter at\xE9 ${String(MAX_INCOME_STATEMENT_ENTRIES_MONTHS)} meses`
    });
  }
});
var incomeStatementEntrySourceSchema = z22.enum(["LEDGER_ENTRY", "TITLE"]);
var incomeStatementEntrySchema = z22.object({
  id: uuidSchema,
  source: incomeStatementEntrySourceSchema,
  companyId: uuidSchema,
  ledgerAccountId: uuidSchema,
  /** Competência (lançamento) ou pagamento/recebimento (título). */
  date: isoDateSchema,
  description: z22.string(),
  /** Efeito no resultado: positivo aumenta, negativo reduz. */
  amount: moneySchema,
  titleId: uuidSchema.nullable(),
  partyName: z22.string().nullable()
});
var incomeStatementEntryListSchema = paginatedResponseSchema(incomeStatementEntrySchema);

// src/finance/income-statement-labels.ts
var INCOME_STATEMENT_ROW_LABELS = {
  GROSS_REVENUE: "Receita bruta",
  REVENUE_DEDUCTIONS: "(\u2212) Dedu\xE7\xF5es da receita",
  NET_REVENUE: "= Receita l\xEDquida",
  COSTS: "(\u2212) Custos",
  GROSS_PROFIT: "= Lucro bruto",
  OPERATING_EXPENSES: "(\u2212) Despesas operacionais",
  OPERATING_RESULT: "= Resultado operacional",
  FINANCIAL_RESULT: "(\xB1) Resultado financeiro",
  UNCLASSIFIED: "(\xB1) Classifica\xE7\xF5es sem linha na DRE",
  PROFIT_BEFORE_TAXES: "= Resultado antes do IRPJ e da CSLL",
  INCOME_TAXES: "(\u2212) IRPJ e CSLL",
  NET_PROFIT: "= Lucro l\xEDquido"
};
var ACCOUNTING_BASIS_LABELS = {
  ACCRUAL: "Compet\xEAncia",
  CASH: "Caixa"
};
var STATEMENT_PERIOD_LABELS = {
  MONTH: "Mensal",
  QUARTER: "Trimestral",
  YEAR: "Anual",
  YEAR_TO_DATE: "Acumulado do ano"
};

// src/finance/party.ts
import { z as z26 } from "zod";

// src/primitives/cpf.ts
import { z as z23 } from "zod";
var CPF_FORMAT = /^\d{11}$/;
var BASE_LENGTH = 9;
function checkDigit2(digits) {
  let sum = 0;
  const firstWeight = digits.length + 1;
  for (let index = 0; index < digits.length; index += 1) {
    sum += Number(digits.charAt(index)) * (firstWeight - index);
  }
  const remainder = sum * 10 % 11;
  return remainder === 10 ? 0 : remainder;
}
function isValidCpf(value) {
  if (!CPF_FORMAT.test(value)) return false;
  if (new Set(value).size === 1) return false;
  const base = value.slice(0, BASE_LENGTH);
  const first = checkDigit2(base);
  const second = checkDigit2(base + String(first));
  return value.slice(BASE_LENGTH) === `${String(first)}${String(second)}`;
}
var cpfSchema = z23.string().refine(isValidCpf, { message: "CPF inv\xE1lido" }).meta({ description: "CPF com 11 d\xEDgitos, sem m\xE1scara", example: "52998224725" });
var taxIdSchema = z23.string().overwrite((value) => value.toUpperCase()).refine((value) => isValidCpf(value) || isValidCnpj(value), {
  message: "Documento inv\xE1lido: informe CPF (11 d\xEDgitos) ou CNPJ (14 caracteres), sem m\xE1scara"
}).meta({ description: "CPF ou CNPJ sem m\xE1scara", example: "11222333000181" });

// src/primitives/email.ts
import { z as z24 } from "zod";
var EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
var EMAIL_MAX_LENGTH = 254;
function isValidEmailAddress(value) {
  return value.length <= EMAIL_MAX_LENGTH && EMAIL_FORMAT.test(value);
}
var emailAddressSchema = z24.string().trim().toLowerCase().refine(isValidEmailAddress, { message: "E-mail inv\xE1lido" }).meta({ description: "E-mail, normalizado para min\xFAsculas", example: "contato@example.test" });

// src/primitives/phone.ts
import { z as z25 } from "zod";
var BRAZILIAN_PHONE_FORMAT = /^[1-9]{2}(9\d{8}|[2-8]\d{7})$/;
function isValidBrazilianPhone(value) {
  return BRAZILIAN_PHONE_FORMAT.test(value);
}
var brazilianPhoneSchema = z25.string().refine(isValidBrazilianPhone, {
  message: "Telefone inv\xE1lido: informe DDD e n\xFAmero, s\xF3 d\xEDgitos"
}).meta({ description: "Telefone brasileiro com DDD, s\xF3 d\xEDgitos", example: "11987654321" });

// src/finance/party.ts
var PARTY_TERM_MAX_DAYS = 365;
var PARTY_NOTES_MAX_LENGTH = 2e3;
var PARTY_CONTACT_NAME_MAX_LENGTH = 120;
var partyNameSchema = z26.string().trim().min(1).max(200);
var partyTermDaysSchema = z26.number().int().min(0).max(PARTY_TERM_MAX_DAYS).meta({ description: "Prazo padr\xE3o em dias (0 a 365)", example: 30 });
var partyRoleDefaultsInputSchema = z26.strictObject({
  termDays: partyTermDaysSchema.nullable(),
  ledgerAccountId: uuidSchema.nullable()
});
var partyDetailsFields = {
  kind: partyKindSchema,
  name: partyNameSchema,
  tradeName: partyNameSchema.nullish(),
  document: taxIdSchema.nullish(),
  contactName: z26.string().trim().min(1).max(PARTY_CONTACT_NAME_MAX_LENGTH).nullish(),
  contactEmail: emailAddressSchema.nullish(),
  contactPhone: brazilianPhoneSchema.nullish(),
  notes: z26.string().trim().min(1).max(PARTY_NOTES_MAX_LENGTH).nullish()
};
var partyDetailsShape = {
  ...partyDetailsFields,
  supplierDefaults: partyRoleDefaultsInputSchema.nullish(),
  customerDefaults: partyRoleDefaultsInputSchema.nullish()
};
var ROLE_DEFAULTS = [
  { field: "supplierDefaults", kinds: ["SUPPLIER", "BOTH"], role: "fornecedor" },
  { field: "customerDefaults", kinds: ["CUSTOMER", "BOTH"], role: "cliente" }
];
var hasDefaults = (value) => value !== void 0 && value !== null && (value.termDays !== null || value.ledgerAccountId !== null);
function refineRoleDefaults(party, context) {
  for (const { field, kinds, role } of ROLE_DEFAULTS) {
    if (hasDefaults(party[field]) && !kinds.includes(party.kind)) {
      context.addIssue({
        code: "custom",
        path: [field],
        message: `Condi\xE7\xF5es de ${role} s\xF3 valem para quem \xE9 ${role}`
      });
    }
  }
}
var createPartyRequestSchema = z26.strictObject(partyDetailsShape).superRefine(refineRoleDefaults);
var updatePartyRequestSchema = z26.strictObject({ ...partyDetailsShape, version: z26.number().int().min(1) }).superRefine(refineRoleDefaults);
var partyLedgerAccountSchema = z26.object({
  id: uuidSchema,
  code: z26.string(),
  name: z26.string()
});
var partyRoleDefaultsSchema = z26.object({
  termDays: z26.number().int().nullable(),
  ledgerAccount: partyLedgerAccountSchema.nullable()
});
var partySchema = z26.object({
  id: uuidSchema,
  organizationId: uuidSchema,
  kind: partyKindSchema,
  name: z26.string(),
  tradeName: z26.string().nullable(),
  document: z26.string().nullable(),
  contactName: z26.string().nullable(),
  contactEmail: z26.string().nullable(),
  contactPhone: z26.string().nullable(),
  supplierDefaults: partyRoleDefaultsSchema.nullable(),
  customerDefaults: partyRoleDefaultsSchema.nullable(),
  notes: z26.string().nullable(),
  status: recordStatusSchema,
  version: z26.number().int(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema
});
var partySortFieldSchema = z26.enum(["name", "createdAt"]);
var listPartiesQuerySchema = listQuerySchema(partySortFieldSchema.default("name")).extend({
  kind: partyKindSchema.exclude(["BOTH"]).optional(),
  status: recordStatusSchema.optional(),
  document: taxIdSchema.optional()
});
var partyCnpjLookupResponseSchema = z26.object({
  cnpj: cnpjSchema,
  legalName: z26.string(),
  tradeName: z26.string().nullable(),
  registrationStatus: cnpjRegistrationStatusSchema.nullable()
});
var partyIdParamsSchema = z26.object({ partyId: uuidSchema });
var partyListResponseSchema = paginatedResponseSchema(partySchema);

// src/finance/party-import.ts
import { z as z27 } from "zod";
var PARTY_IMPORT_MAX_ROWS = 1e3;
var ROLE_FIELDS = [
  {
    fields: ["supplierTermDays", "supplierLedgerAccountCode"],
    kinds: ["SUPPLIER", "BOTH"],
    role: "fornecedor"
  },
  {
    fields: ["customerTermDays", "customerLedgerAccountCode"],
    kinds: ["CUSTOMER", "BOTH"],
    role: "cliente"
  }
];
var partyImportRowSchema = z27.strictObject({
  line: z27.number().int().min(1),
  ...partyDetailsFields,
  supplierTermDays: partyTermDaysSchema.nullish(),
  supplierLedgerAccountCode: ledgerAccountCodeSchema.nullish(),
  customerTermDays: partyTermDaysSchema.nullish(),
  customerLedgerAccountCode: ledgerAccountCodeSchema.nullish()
}).superRefine(
  (row, context) => {
    if (!partyKindSchema.safeParse(row.kind).success) return;
    for (const { fields, kinds, role } of ROLE_FIELDS) {
      if (kinds.includes(row.kind)) continue;
      for (const field of fields) {
        if (row[field] === void 0 || row[field] === null) continue;
        context.addIssue({
          code: "custom",
          path: [field],
          message: `Condi\xE7\xF5es de ${role} s\xF3 valem para quem \xE9 ${role}`
        });
      }
    }
  },
  // Roda mesmo com outros erros na linha: a pré-visualização mostra todos de uma vez.
  { when: () => true }
);
var partyImportRequestSchema = z27.strictObject({ rows: z27.array(partyImportRowSchema).min(1).max(PARTY_IMPORT_MAX_ROWS) }).superRefine((request, context) => {
  const seen = /* @__PURE__ */ new Set();
  request.rows.forEach((row, index) => {
    if (seen.has(row.line)) {
      context.addIssue({
        code: "custom",
        path: ["rows", index, "line"],
        message: "Linha repetida no envio"
      });
    }
    seen.add(row.line);
  });
});
var partyImportRowStatusSchema = z27.enum(["NEW", "DUPLICATE", "INVALID"]);
var partyImportWarningCodeSchema = z27.enum([
  "NAME_ALREADY_EXISTS",
  "NAME_REPEATED_IN_FILE"
]);
var partyImportRowResultSchema = z27.object({
  line: z27.number().int(),
  status: partyImportRowStatusSchema,
  duplicateOf: z27.object({ id: uuidSchema, name: z27.string() }).nullable(),
  duplicateOfLine: z27.number().int().nullable(),
  errors: z27.array(z27.object({ field: z27.string(), code: z27.string(), message: z27.string() })),
  warnings: z27.array(z27.object({ code: partyImportWarningCodeSchema, message: z27.string() }))
});
var partyImportPreviewResponseSchema = z27.object({
  rows: z27.array(partyImportRowResultSchema),
  totals: z27.object({
    new: z27.number().int(),
    duplicate: z27.number().int(),
    invalid: z27.number().int()
  })
});
var partyImportResultSchema = z27.object({
  created: z27.array(z27.object({ line: z27.number().int(), partyId: uuidSchema })),
  skipped: z27.array(partyImportRowResultSchema),
  rejected: z27.array(partyImportRowResultSchema)
});

// src/finance/report-export.ts
import { z as z28 } from "zod";
var reportExportFormatSchema = z28.enum(["PDF", "XLSX"]);
var reportExportKindSchema = z28.enum(["INCOME_STATEMENT"]);
var reportExportStatusSchema = z28.enum(["PENDING", "PROCESSING", "READY", "FAILED"]);
var incomeStatementAnalysisSchema = z28.object({
  /** % da receita líquida. */
  vertical: z28.boolean().default(true),
  /** Variação contra o mês anterior. */
  previousMonth: z28.boolean().default(false),
  /** Variação contra o mesmo mês do ano anterior. */
  previousYear: z28.boolean().default(false)
});
var incomeStatementExportRequestSchema = incomeStatementFiltersSchema.extend({
  format: reportExportFormatSchema,
  analysis: incomeStatementAnalysisSchema.prefault({}),
  ...reportScopeQueryShape
}).superRefine(refineReportScope);
var reportExportSchema = z28.object({
  id: uuidSchema,
  report: reportExportKindSchema,
  format: reportExportFormatSchema,
  status: reportExportStatusSchema,
  fileName: z28.string(),
  /** Tamanho do arquivo pronto, em bytes; `null` antes de ficar pronto. */
  byteSize: z28.number().int().nonnegative().nullable(),
  /** Motivo da falha para exibir; `null` fora de `FAILED`. */
  failureMessage: z28.string().nullable(),
  requestedAt: isoDateTimeSchema,
  completedAt: isoDateTimeSchema.nullable(),
  /** Prazo para baixar; depois a exportação é descartada. O download também a apaga. */
  expiresAt: isoDateTimeSchema
});
var reportExportIdParamsSchema = z28.object({ id: uuidSchema });
var reportExportListSchema = z28.object({ items: z28.array(reportExportSchema) });

// src/finance/title.ts
import { z as z30 } from "zod";

// src/primitives/percentage.ts
import { z as z29 } from "zod";
var PERCENTAGE_FORMAT = /^\d{1,3}(?:\.\d{1,6})?$/;
function isValidPercentage(value) {
  if (!PERCENTAGE_FORMAT.test(value)) return false;
  const [integer = "", fraction = ""] = value.split(".");
  const isZero = /^0+$/.test(integer) && /^0*$/.test(fraction);
  const overHundred = Number(integer) > 100 || Number(integer) === 100 && /[1-9]/.test(fraction);
  return !isZero && !overHundred;
}
var percentageSchema = z29.string().refine(isValidPercentage, {
  message: "Percentual inv\xE1lido: use valor maior que zero e at\xE9 100, com at\xE9 6 casas e ponto"
}).meta({ description: "Percentual decimal em string, de 0 (exclusivo) a 100", example: "33.5" });

// src/finance/title.ts
var MAX_TITLE_SERIES_SIZE = 120;
var MAX_TITLE_ALLOCATIONS = 50;
var positiveMoneySchema = moneySchema.refine(
  (value) => !value.startsWith("-") && value !== "0.00",
  { message: "O valor deve ser maior que zero" }
);
var seriesSizeSchema = z30.number().int().min(2).max(MAX_TITLE_SERIES_SIZE);
var titleAllocationInputSchema = z30.strictObject({
  companyId: uuidSchema,
  ledgerAccountId: uuidSchema,
  costCenterId: uuidSchema.nullish(),
  percentage: percentageSchema.optional(),
  amount: positiveMoneySchema.optional()
});
var titleAllocationRequestSchema = z30.strictObject({
  method: allocationMethodSchema,
  items: z30.array(titleAllocationInputSchema).min(1).max(MAX_TITLE_ALLOCATIONS)
}).superRefine((allocation, context) => {
  allocation.items.forEach((item, index) => {
    const expected = allocation.method === "PERCENTAGE" ? "percentage" : "amount";
    const unexpected = allocation.method === "PERCENTAGE" ? "amount" : "percentage";
    if (item[expected] === void 0) {
      context.addIssue({
        code: "custom",
        path: ["items", index, expected],
        message: `Rateio por ${allocation.method === "PERCENTAGE" ? "percentual" : "valor"} exige ${expected} em cada linha`
      });
    }
    if (item[unexpected] !== void 0) {
      context.addIssue({
        code: "custom",
        path: ["items", index, unexpected],
        message: `N\xE3o informe ${unexpected} no rateio por ${allocation.method === "PERCENTAGE" ? "percentual" : "valor"}`
      });
    }
  });
});
var titleFieldsShape = {
  partyId: uuidSchema,
  ledgerAccountId: uuidSchema,
  costCenterId: uuidSchema.nullish(),
  description: z30.string().trim().min(1).max(500),
  documentNumber: z30.string().trim().min(1).max(60).nullish(),
  amount: positiveMoneySchema,
  issueDate: isoDateSchema,
  competenceDate: isoDateSchema,
  dueDate: isoDateSchema,
  notes: z30.string().trim().min(1).max(2e3).nullish(),
  allocation: titleAllocationRequestSchema.optional()
};
function refineDueDate(title, context) {
  if (title.dueDate < title.issueDate) {
    context.addIssue({
      code: "custom",
      path: ["dueDate"],
      message: "O vencimento n\xE3o pode ser anterior \xE0 emiss\xE3o"
    });
  }
}
var createTitleRequestSchema = z30.strictObject({
  kind: titleKindSchema,
  companyId: uuidSchema,
  ...titleFieldsShape,
  installments: z30.strictObject({ count: seriesSizeSchema }).optional(),
  recurrence: z30.strictObject({ frequency: recurrenceFrequencySchema, occurrences: seriesSizeSchema }).optional()
}).superRefine((title, context) => {
  refineDueDate(title, context);
  if (title.installments !== void 0 && title.recurrence !== void 0) {
    context.addIssue({
      code: "custom",
      path: ["recurrence"],
      message: "Informe parcelamento ou recorr\xEAncia, n\xE3o os dois"
    });
  }
});
var updateTitleRequestSchema = z30.strictObject({ ...titleFieldsShape, version: z30.number().int().min(1) }).superRefine(refineDueDate);
var TITLE_CANCEL_REASON_MIN = 5;
var TITLE_CANCEL_REASON_MAX = 500;
var cancelTitleRequestSchema = z30.strictObject({
  reason: z30.string().trim().min(
    TITLE_CANCEL_REASON_MIN,
    `Informe o motivo em ao menos ${String(TITLE_CANCEL_REASON_MIN)} caracteres`
  ).max(TITLE_CANCEL_REASON_MAX),
  version: z30.number().int().min(1)
});
var titleAllocationSchema = z30.object({
  id: uuidSchema,
  companyId: uuidSchema,
  ledgerAccountId: uuidSchema,
  costCenterId: uuidSchema.nullable(),
  percentage: z30.string().nullable(),
  amount: moneySchema
});
var titleSchema = z30.object({
  id: uuidSchema,
  organizationId: uuidSchema,
  companyId: uuidSchema,
  kind: titleKindSchema,
  party: z30.object({ id: uuidSchema, name: z30.string() }),
  ledgerAccountId: uuidSchema,
  costCenterId: uuidSchema.nullable(),
  description: z30.string(),
  documentNumber: z30.string().nullable(),
  amount: moneySchema,
  currency: z30.string().length(3),
  issueDate: isoDateSchema,
  competenceDate: isoDateSchema,
  dueDate: isoDateSchema,
  status: titleStatusSchema,
  overdue: z30.boolean(),
  settledOn: isoDateSchema.nullable(),
  installment: z30.object({
    groupId: uuidSchema,
    number: z30.number().int().min(1),
    count: z30.number().int().min(2)
  }).nullable(),
  recurrence: z30.object({
    groupId: uuidSchema,
    frequency: recurrenceFrequencySchema,
    number: z30.number().int().min(1)
  }).nullable(),
  notes: z30.string().nullable(),
  allocationMethod: allocationMethodSchema,
  allocations: z30.array(titleAllocationSchema),
  /**
   * Período fechado (ADR-0025) em alguma empresa do título ou do rateio: no mês da competência
   * (bloqueia editar e cancelar) e no mês do pagamento (bloqueia desfazer a baixa).
   */
  periodLock: z30.object({ competenceClosed: z30.boolean(), settlementClosed: z30.boolean() }),
  version: z30.number().int().min(1),
  createdByUserId: uuidSchema,
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema
});
var createTitlesResponseSchema = z30.object({ items: z30.array(titleSchema) });
var titlePreviewItemSchema = z30.object({
  amount: moneySchema,
  issueDate: isoDateSchema,
  competenceDate: isoDateSchema,
  dueDate: isoDateSchema,
  installment: z30.object({ number: z30.number().int().min(1), count: z30.number().int().min(2) }).nullable(),
  recurrence: z30.object({ frequency: recurrenceFrequencySchema, number: z30.number().int().min(1) }).nullable(),
  allocations: z30.array(titleAllocationSchema.omit({ id: true }))
});
var titlePreviewResponseSchema = z30.object({ items: z30.array(titlePreviewItemSchema) });
var titleSortFieldSchema = z30.enum([
  "dueDate",
  "competenceDate",
  "issueDate",
  "amount",
  "description",
  "createdAt"
]);
var listTitlesQuerySchema = listQuerySchema(titleSortFieldSchema.default("dueDate")).extend({
  kind: titleKindSchema.optional(),
  companyId: uuidSchema.optional(),
  companyIds: companyIdsFilterSchema.optional(),
  status: titleStatusFilterSchema.optional(),
  dateField: titleDateFieldSchema.default("DUE"),
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  ledgerAccountId: uuidSchema.optional(),
  costCenterId: uuidSchema.optional(),
  partyId: uuidSchema.optional(),
  seriesGroupId: uuidSchema.optional()
}).superRefine((query, context) => {
  if (query.from !== void 0 && query.to !== void 0 && query.to < query.from) {
    context.addIssue({
      code: "custom",
      path: ["to"],
      message: "O fim do per\xEDodo n\xE3o pode ser anterior ao in\xEDcio"
    });
  }
});
var titleTotalsSchema = z30.object({
  open: moneySchema,
  overdue: moneySchema,
  settled: moneySchema
});
var titleListResponseSchema = paginatedResponseSchema(titleSchema).extend({
  totals: z30.object({ payable: titleTotalsSchema, receivable: titleTotalsSchema })
});
var titleIdParamsSchema = z30.object({ titleId: uuidSchema });

// src/finance/nfe-preview.ts
import { z as z31 } from "zod";
var NFE_NAMESPACE = "http://www.portalfiscal.inf.br/nfe";
var nfePreviewFieldsSchema = z31.object({
  kind: titleKindSchema,
  companyId: uuidSchema.optional()
});
var nfeWarningCodeSchema = z31.enum([
  "NO_DUPLICATES",
  "DUPLICATES_TOTAL_DIFFERS",
  "DUPLICATE_WITHOUT_DUE_DATE",
  "DUE_DATE_BEFORE_ISSUE",
  "INSTALLMENTS_NOT_REPRODUCIBLE",
  "COUNTERPARTY_MISSING",
  "COUNTERPARTY_NOT_FOUND",
  "COUNTERPARTY_INACTIVE",
  "COUNTERPARTY_ROLE_MISMATCH",
  "COMPANY_NOT_FOUND",
  "COMPANY_MISMATCH",
  "ISSUE_DATE_IN_CLOSED_PERIOD"
]);
var nfeMissingFieldSchema = z31.enum([
  "number",
  "issueDate",
  "totalAmount",
  "issuer.document",
  "issuer.name",
  "recipient"
]);
var nfePartySchema = z31.object({
  documentType: z31.enum(["CNPJ", "CPF", "FOREIGN"]).nullable(),
  document: z31.string().nullable(),
  name: z31.string().nullable(),
  tradeName: z31.string().nullable()
});
var nfeDuplicateSchema = z31.object({
  number: z31.string().nullable(),
  dueDate: isoDateSchema.nullable(),
  amount: moneySchema.nullable()
});
var nfeSuggestionsSchema = z31.object({
  partyId: uuidSchema.nullable(),
  documentNumber: z31.string().nullable(),
  amount: moneySchema.nullable(),
  issueDate: isoDateSchema.nullable(),
  dueDate: isoDateSchema.nullable(),
  installmentCount: z31.number().int().min(2).max(MAX_TITLE_SERIES_SIZE).nullable()
});
var nfeInvoicePreviewSchema = z31.object({
  version: z31.string(),
  model: z31.string(),
  series: z31.string().nullable(),
  number: z31.string().nullable(),
  accessKey: z31.string().nullable(),
  issueDate: isoDateSchema.nullable(),
  totalAmount: moneySchema.nullable(),
  issuer: nfePartySchema,
  recipient: nfePartySchema.nullable(),
  duplicates: z31.array(nfeDuplicateSchema),
  counterparty: z31.object({
    role: z31.enum(["ISSUER", "RECIPIENT"]),
    partyId: uuidSchema.nullable(),
    partyName: z31.string().nullable()
  }),
  company: z31.object({
    /** Empresa da organização com o CNPJ da nota, se estiver no escopo de quem lança. */
    companyId: uuidSchema.nullable(),
    /** Se a empresa da nota é a escolhida no formulário (`null` = nenhuma escolhida). */
    matchesSelected: z31.boolean().nullable()
  }),
  suggestions: nfeSuggestionsSchema,
  missingFields: z31.array(nfeMissingFieldSchema),
  warnings: z31.array(z31.object({ code: nfeWarningCodeSchema, message: z31.string() }))
});
var nfePreviewSchema = z31.object({
  recognized: z31.boolean(),
  reason: z31.string().nullable(),
  invoice: nfeInvoicePreviewSchema.nullable()
});

// src/finance/title-attachment.ts
import { z as z32 } from "zod";
var ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
var ATTACHMENT_ACCESS_URL_TTL_SECONDS = 300;
var ATTACHMENT_FILE_NAME_MAX_LENGTH = 255;
var attachmentContentTypeSchema = z32.enum([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/xml"
]);
var ATTACHMENT_EXTENSIONS = {
  "application/pdf": ["pdf"],
  "image/jpeg": ["jpg", "jpeg"],
  "image/png": ["png"],
  "image/webp": ["webp"],
  "application/xml": ["xml"]
};
var ATTACHMENT_ACCEPT = [
  ...Object.values(ATTACHMENT_EXTENSIONS).flat().map((extension) => `.${extension}`),
  ...attachmentContentTypeSchema.options
].join(",");
function attachmentContentTypeForFileName(fileName) {
  const dot = fileName.lastIndexOf(".");
  if (dot < 0 || dot === fileName.length - 1) return null;
  const extension = fileName.slice(dot + 1).toLowerCase();
  for (const contentType of attachmentContentTypeSchema.options) {
    if (ATTACHMENT_EXTENSIONS[contentType].includes(extension)) return contentType;
  }
  return null;
}
var attachmentDispositionSchema = z32.enum(["inline", "attachment"]);
var titleAttachmentSchema = z32.object({
  id: uuidSchema,
  titleId: uuidSchema,
  fileName: z32.string().min(1).max(ATTACHMENT_FILE_NAME_MAX_LENGTH),
  contentType: attachmentContentTypeSchema,
  byteSize: z32.number().int().min(1).max(ATTACHMENT_MAX_BYTES),
  sha256: z32.string().regex(/^[0-9a-f]{64}$/),
  uploadedByUserId: uuidSchema,
  uploadedAt: isoDateTimeSchema
});
var titleAttachmentListSchema = z32.object({ items: z32.array(titleAttachmentSchema) });
var titleAttachmentParamsSchema = z32.object({
  titleId: uuidSchema,
  attachmentId: uuidSchema
});
var attachmentAccessUrlRequestSchema = z32.strictObject({
  disposition: attachmentDispositionSchema.default("attachment")
});
var attachmentAccessUrlSchema = z32.object({
  url: z32.url(),
  expiresAt: isoDateTimeSchema
});

// src/finance/title-import.ts
import { z as z33 } from "zod";
var TITLE_IMPORT_MAX_ROWS = 1e3;
var titleImportRowSchema = z33.strictObject({
  line: z33.number().int().min(1),
  kind: titleKindSchema,
  companyCnpj: cnpjSchema,
  partyDocument: z33.string().trim().regex(/^[0-9A-Z]{11,14}$/, { message: "CPF ou CNPJ inv\xE1lido" }).nullish(),
  partyName: z33.string().trim().min(1).max(200).nullish(),
  ledgerAccountCode: ledgerAccountCodeSchema,
  costCenterCode: costCenterCodeSchema.nullish(),
  description: z33.string().trim().min(1).max(500),
  documentNumber: z33.string().trim().min(1).max(60).nullish(),
  amount: positiveMoneySchema,
  issueDate: isoDateSchema,
  competenceDate: isoDateSchema.nullish(),
  dueDate: isoDateSchema,
  notes: z33.string().trim().min(1).max(2e3).nullish()
}).superRefine(
  (row, context) => {
    const hasDocument = row.partyDocument !== void 0 && row.partyDocument !== null;
    const hasName = row.partyName !== void 0 && row.partyName !== null;
    if (!hasDocument && !hasName) {
      context.addIssue({
        code: "custom",
        path: ["partyName"],
        message: "Informe o CPF/CNPJ ou o nome da contraparte"
      });
    }
    if (typeof row.dueDate === "string" && typeof row.issueDate === "string") {
      if (row.dueDate < row.issueDate) {
        context.addIssue({
          code: "custom",
          path: ["dueDate"],
          message: "O vencimento n\xE3o pode ser anterior \xE0 emiss\xE3o"
        });
      }
    }
  },
  // Roda mesmo com outros erros na linha: a validação mostra todos de uma vez.
  { when: () => true }
);
var titleImportRequestSchema = z33.strictObject({ rows: z33.array(titleImportRowSchema).min(1).max(TITLE_IMPORT_MAX_ROWS) }).superRefine((request, context) => {
  const seen = /* @__PURE__ */ new Set();
  request.rows.forEach((row, index) => {
    if (seen.has(row.line)) {
      context.addIssue({
        code: "custom",
        path: ["rows", index, "line"],
        message: "Linha repetida no envio"
      });
    }
    seen.add(row.line);
  });
});
var titleImportRowStatusSchema = z33.enum(["NEW", "DUPLICATE", "INVALID"]);
var titleImportWarningCodeSchema = z33.enum(["POSSIBLE_DUPLICATE"]);
var titleImportRowResultSchema = z33.object({
  line: z33.number().int(),
  status: titleImportRowStatusSchema,
  duplicateOf: z33.object({ id: uuidSchema, description: z33.string() }).nullable(),
  duplicateOfLine: z33.number().int().nullable(),
  errors: z33.array(z33.object({ field: z33.string(), code: z33.string(), message: z33.string() })),
  warnings: z33.array(z33.object({ code: titleImportWarningCodeSchema, message: z33.string() }))
});
var titleImportPreviewResponseSchema = z33.object({
  rows: z33.array(titleImportRowResultSchema),
  totals: z33.object({
    new: z33.number().int(),
    duplicate: z33.number().int(),
    invalid: z33.number().int()
  })
});
var titleImportResultSchema = z33.object({
  created: z33.array(z33.object({ line: z33.number().int(), titleId: uuidSchema })),
  skipped: z33.array(titleImportRowResultSchema),
  rejected: z33.array(titleImportRowResultSchema)
});

// src/platform/platform.ts
import { z as z35 } from "zod";

// src/user/user.ts
import { z as z34 } from "zod";
var EMAIL_FORMAT2 = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
var MAX_PRESENTED_TOKEN_LENGTH = 1024;
var userStatusSchema = z34.enum(["ACTIVE", "INVITED", "INVITATION_EXPIRED", "INACTIVE"]);
var USER_STATUS_NAMES = {
  ACTIVE: "Ativo",
  INVITED: "Convite pendente",
  INVITATION_EXPIRED: "Convite expirado",
  INACTIVE: "Inativo"
};
var inviteEmailSchema = emailInputSchema.refine((value) => EMAIL_FORMAT2.test(value), {
  message: "E-mail inv\xE1lido"
});
var displayNameSchema = z34.string().trim().min(1, { message: "Informe o nome" }).max(200, { message: "Use at\xE9 200 caracteres" });
var userAccessShape = {
  roleCode: roleCodeSchema,
  allCompanies: z34.boolean(),
  companyIds: z34.array(uuidSchema).max(MAX_FILTERED_COMPANIES, { message: "Selecione at\xE9 200 empresas" })
};
function checkUserAccess(access, context) {
  if (!access.allCompanies && access.companyIds.length === 0) {
    context.addIssue({
      code: "custom",
      path: ["companyIds"],
      message: "Selecione ao menos uma empresa ou libere todas"
    });
  }
  if (access.allCompanies && access.companyIds.length > 0) {
    context.addIssue({
      code: "custom",
      path: ["companyIds"],
      message: "N\xE3o selecione empresas quando todas est\xE3o liberadas"
    });
  }
  if (access.allCompanies && access.roleCode === "BRANCH_MANAGER") {
    context.addIssue({
      code: "custom",
      path: ["allCompanies"],
      message: "O Gestor de filial vale s\xF3 para as empresas selecionadas"
    });
  }
}
var userAccessSchema = z34.object(userAccessShape).superRefine(checkUserAccess);
var inviteUserRequestSchema = z34.strictObject({ email: inviteEmailSchema, displayName: displayNameSchema, ...userAccessShape }).superRefine(checkUserAccess);
var updateUserAccessRequestSchema = z34.strictObject({ ...userAccessShape, version: z34.number().int().min(1) }).superRefine(checkUserAccess);
var userSchema = z34.object({
  id: uuidSchema,
  email: z34.string(),
  displayName: z34.string(),
  roleCode: roleCodeSchema,
  allCompanies: z34.boolean(),
  companyIds: z34.array(uuidSchema),
  status: userStatusSchema,
  lastLoginAt: isoDateTimeSchema.nullable(),
  /** Fim da validade do convite pendente (vigente ou vencido); nulo para quem tem senha. */
  invitationExpiresAt: isoDateTimeSchema.nullable(),
  version: z34.number().int().min(1)
});
var userSortFieldSchema = z34.enum(["displayName", "email", "lastLoginAt"]);
var listUsersQuerySchema = listQuerySchema(
  userSortFieldSchema.default("displayName")
).extend({
  status: userStatusSchema.optional(),
  roleCode: roleCodeSchema.optional()
});
var userIdParamsSchema = z34.object({ userId: uuidSchema });
var userListResponseSchema = paginatedResponseSchema(userSchema);
var acceptInvitationRequestSchema = z34.object({
  token: z34.string().min(1).max(MAX_PRESENTED_TOKEN_LENGTH),
  newPassword: newPasswordSchema
});

// src/platform/platform.ts
var ORGANIZATION_SLUG_MAX_LENGTH = 63;
var SLUG_FORMAT = /^[a-z0-9]+(-[a-z0-9]+)*$/;
var organizationNameSchema = z35.string().trim().min(1, { message: "Informe o nome da organiza\xE7\xE3o" }).max(200, { message: "Use at\xE9 200 caracteres" });
var organizationSlugSchema = z35.string().max(ORGANIZATION_SLUG_MAX_LENGTH, { message: "Use at\xE9 63 caracteres" }).regex(SLUG_FORMAT, { message: "Use letras min\xFAsculas, n\xFAmeros e h\xEDfens" });
var administratorRequestShape = { email: inviteEmailSchema, displayName: displayNameSchema };
var createOrganizationRequestSchema = z35.strictObject({
  name: organizationNameSchema,
  slug: organizationSlugSchema,
  administrator: z35.strictObject(administratorRequestShape)
});
var addOrganizationAdministratorRequestSchema = z35.strictObject(administratorRequestShape);
var promotePlatformAdministratorRequestSchema = z35.strictObject(administratorRequestShape);
var organizationSummarySchema = z35.object({
  id: uuidSchema,
  name: z35.string(),
  slug: z35.string(),
  status: recordStatusSchema,
  /** Administradores com acesso ativo; zero sinaliza organização sem quem a administre. */
  activeAdministrators: z35.number().int().min(0),
  createdAt: isoDateTimeSchema
});
var organizationSortFieldSchema = z35.enum(["name", "createdAt"]);
var listOrganizationsQuerySchema = listQuerySchema(
  organizationSortFieldSchema.default("name")
);
var organizationListResponseSchema = paginatedResponseSchema(organizationSummarySchema);
var organizationIdParamsSchema = z35.object({ organizationId: uuidSchema });
var organizationAdministratorSchema = z35.object({
  userId: uuidSchema,
  email: z35.string(),
  displayName: z35.string(),
  status: userStatusSchema,
  /** Fim da validade do convite pendente (vigente ou vencido); nulo para quem tem senha. */
  invitationExpiresAt: isoDateTimeSchema.nullable()
});
var organizationAdministratorListResponseSchema = z35.object({
  items: z35.array(organizationAdministratorSchema)
});
var createOrganizationResponseSchema = z35.object({
  organization: organizationSummarySchema,
  administrator: organizationAdministratorSchema
});
var platformAdministratorSchema = z35.object({
  userId: uuidSchema,
  email: z35.string(),
  displayName: z35.string(),
  grantedAt: isoDateTimeSchema
});
var platformAdministratorListResponseSchema = z35.object({
  items: z35.array(platformAdministratorSchema)
});

// src/primitives/problem-details.ts
import { z as z36 } from "zod";
var fieldErrorSchema = z36.object({
  field: z36.string(),
  message: z36.string(),
  code: z36.string().optional()
});
var problemDetailsSchema = z36.object({
  type: z36.string().default("about:blank"),
  title: z36.string(),
  status: z36.number().int().min(100).max(599),
  detail: z36.string().optional(),
  instance: z36.string().optional(),
  traceId: z36.string().optional(),
  errors: z36.array(fieldErrorSchema).optional()
}).meta({ id: "ProblemDetails" });
export {
  ACCOUNTING_BASIS_LABELS,
  ACCOUNTING_PERIOD_CLOSED_CODE,
  ADDRESS_LIMITS,
  ATTACHMENT_ACCEPT,
  ATTACHMENT_ACCESS_URL_TTL_SECONDS,
  ATTACHMENT_EXTENSIONS,
  ATTACHMENT_FILE_NAME_MAX_LENGTH,
  ATTACHMENT_MAX_BYTES,
  BALANCE_SHEET_GROUP_CATEGORIES,
  BANK_STATEMENT_MAX_TRANSACTIONS,
  BRAZILIAN_BANKS,
  BRAZILIAN_STATES,
  CHART_TEMPLATE_TAX_REGIMES,
  COMPANY_LOGO_MAX_BYTES,
  CSRF_HEADER_NAME,
  CSRF_HEADER_VALUE,
  DEFAULT_PAGE_SIZE,
  EMAIL_MAX_LENGTH,
  IMPORT_MAX_HEADER_ROW,
  INCOME_STATEMENT_ROW_LABELS,
  LEDGER_ACCOUNT_TYPE_RULES,
  MAX_FILTERED_COMPANIES,
  MAX_INCOME_STATEMENT_ENTRIES_MONTHS,
  MAX_LEDGER_ACCOUNT_LEVEL,
  MAX_PAGE_SIZE,
  MAX_RECONCILIATION_SUGGESTIONS,
  MAX_SEARCH_LENGTH,
  MAX_TITLE_ALLOCATIONS,
  MAX_TITLE_SERIES_SIZE,
  MONEY_MAX_INTEGER_DIGITS,
  MONEY_SCALE,
  NFE_NAMESPACE,
  ORGANIZATION_HEADER_NAME,
  ORGANIZATION_SLUG_MAX_LENGTH,
  ORGANIZATION_WIDE_PERMISSIONS,
  PARTY_CONTACT_NAME_MAX_LENGTH,
  PARTY_IMPORT_MAX_ROWS,
  PARTY_NOTES_MAX_LENGTH,
  PARTY_TERM_MAX_DAYS,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  PERIOD_REOPEN_JUSTIFICATION_MAX,
  PERIOD_REOPEN_JUSTIFICATION_MIN,
  PERMISSIONS,
  RECONCILIATION_WINDOW_DAYS,
  ROLE_CODES,
  ROLE_NAMES,
  ROLE_PERMISSIONS,
  STATEMENT_PERIOD_LABELS,
  TITLE_CANCEL_REASON_MAX,
  TITLE_CANCEL_REASON_MIN,
  TITLE_IMPORT_MAX_ROWS,
  USER_STATUS_NAMES,
  acceptInvitationRequestSchema,
  accountTypesOf,
  accountingBasisSchema,
  accountingPeriodClosingSchema,
  accountingPeriodCompanyParamsSchema,
  accountingPeriodListQuerySchema,
  accountingPeriodListSchema,
  activityCodeSchema,
  addOrganizationAdministratorRequestSchema,
  addressSchema,
  allocationMethodSchema,
  appliedChartTemplateSchema,
  attachmentAccessUrlRequestSchema,
  attachmentAccessUrlSchema,
  attachmentContentTypeForFileName,
  attachmentContentTypeSchema,
  attachmentDispositionSchema,
  auditActionSchema,
  auditEventListSchema,
  auditEventSchema,
  auditFieldChangeSchema,
  auditHistoryQuerySchema,
  authSessionResponseSchema,
  balanceSheetGroupSchema,
  balanceSheetGroupsOf,
  bankAccountIdParamsSchema,
  bankAccountKindSchema,
  bankAccountListResponseSchema,
  bankAccountSchema,
  bankAccountSortFieldSchema,
  bankCodeSchema,
  bankStatementDuplicateReasonSchema,
  bankStatementFlowSchema,
  bankStatementImportRequestSchema,
  bankStatementImportResultSchema,
  bankStatementLineIdParamsSchema,
  bankStatementLineListResponseSchema,
  bankStatementLineSchema,
  bankStatementLineStatusSchema,
  bankStatementLineVersionQuerySchema,
  bankStatementLineVersionRequestSchema,
  bankStatementPreviewResponseSchema,
  bankStatementTransactionResultSchema,
  bankStatementTransactionSchema,
  bankStatementTransactionStatusSchema,
  brazilianBankName,
  brazilianPhoneSchema,
  brazilianStateSchema,
  cancelTitleRequestSchema,
  changePasswordRequestSchema,
  chartTemplateCodeSchema,
  closeAccountingPeriodRequestSchema,
  cnpjLookupParamsSchema,
  cnpjLookupResponseSchema,
  cnpjRegistrationStatusSchema,
  cnpjSchema,
  companyBalanceTotalSchema,
  companyIdParamsSchema,
  companyIdsFilterSchema,
  companyKindSchema,
  companyListResponseSchema,
  companyLogoContentTypeSchema,
  companyLogoUploadSchema,
  companySchema,
  companySortFieldSchema,
  costCenterCodeSchema,
  costCenterIdParamsSchema,
  costCenterListResponseSchema,
  costCenterSchema,
  costCenterSortFieldSchema,
  coversAnyCompany,
  cpfSchema,
  createBankAccountRequestSchema,
  createCompanyRequestSchema,
  createCostCenterRequestSchema,
  createImportTemplateRequestSchema,
  createLedgerAccountRequestSchema,
  createOrganizationRequestSchema,
  createOrganizationResponseSchema,
  createPartyRequestSchema,
  createTitleRequestSchema,
  createTitlesResponseSchema,
  csvDelimiterSchema,
  decimalSeparatorSchema,
  deleteImportTemplateQuerySchema,
  displayNameSchema,
  emailAddressSchema,
  emailInputSchema,
  entryDirectionSchema,
  fieldErrorSchema,
  financeSettingsSchema,
  financialStatementOf,
  ignoreBankStatementLineRequestSchema,
  importDateFormatSchema,
  importFieldSourceSchema,
  importTemplateIdParamsSchema,
  importTemplateKindSchema,
  importTemplateListResponseSchema,
  importTemplateMappingSchema,
  importTemplateSchema,
  importTemplateSettingsSchema,
  incomeStatementAccountKindSchema,
  incomeStatementAccountSchema,
  incomeStatementAnalysisSchema,
  incomeStatementCellSchema,
  incomeStatementComparisonSchema,
  incomeStatementEntriesQuerySchema,
  incomeStatementEntryListSchema,
  incomeStatementEntrySchema,
  incomeStatementEntrySourceSchema,
  incomeStatementExportRequestSchema,
  incomeStatementFiltersSchema,
  incomeStatementLineSchema,
  incomeStatementLinesOf,
  incomeStatementMonthClosingSchema,
  incomeStatementQuerySchema,
  incomeStatementRowCodeSchema,
  incomeStatementRowSchema,
  incomeStatementSchema,
  incomeStatementTotalCellSchema,
  incomeStatementTotalSchema,
  initializeChartOfAccountsRequestSchema,
  initializeChartOfAccountsResponseSchema,
  inviteEmailSchema,
  inviteUserRequestSchema,
  isBalanceSheetCategory,
  isMonthClosed,
  isValidActivityCode,
  isValidBrazilianPhone,
  isValidCnpj,
  isValidCpf,
  isValidEmailAddress,
  isValidMunicipalRegistration,
  isValidPostalCode,
  isValidStateRegistration,
  isoDateSchema,
  isoDateTimeSchema,
  ledgerAccountCategorySchema,
  ledgerAccountClassificationViolations,
  ledgerAccountCodeSchema,
  ledgerAccountIdParamsSchema,
  ledgerAccountKindSchema,
  ledgerAccountListResponseSchema,
  ledgerAccountMappingIssueSchema,
  ledgerAccountMappingSchema,
  ledgerAccountSchema,
  ledgerAccountSortFieldSchema,
  ledgerAccountTypeSchema,
  ledgerNatureSchema,
  listBankAccountsQuerySchema,
  listBankStatementLinesQuerySchema,
  listCompaniesQuerySchema,
  listCostCentersQuerySchema,
  listImportTemplatesQuerySchema,
  listLedgerAccountsQuerySchema,
  listOrganizationsQuerySchema,
  listPartiesQuerySchema,
  listQuerySchema,
  listTitlesQuerySchema,
  listUsersQuerySchema,
  loginRequestSchema,
  moneySchema,
  monthClosingStatusSchema,
  monthSpan,
  municipalRegistrationSchema,
  newPasswordSchema,
  nfeDuplicateSchema,
  nfeInvoicePreviewSchema,
  nfeMissingFieldSchema,
  nfePartySchema,
  nfePreviewFieldsSchema,
  nfePreviewSchema,
  nfeSuggestionsSchema,
  nfeWarningCodeSchema,
  organizationAdministratorListResponseSchema,
  organizationAdministratorSchema,
  organizationIdParamsSchema,
  organizationListResponseSchema,
  organizationNameSchema,
  organizationSlugSchema,
  organizationSortFieldSchema,
  organizationSummarySchema,
  paginatedResponseSchema,
  paginationQuerySchema,
  partyCnpjLookupResponseSchema,
  partyDetailsFields,
  partyIdParamsSchema,
  partyImportPreviewResponseSchema,
  partyImportRequestSchema,
  partyImportResultSchema,
  partyImportRowResultSchema,
  partyImportRowSchema,
  partyImportRowStatusSchema,
  partyImportWarningCodeSchema,
  partyKindSchema,
  partyLedgerAccountSchema,
  partyListResponseSchema,
  partyRoleDefaultsInputSchema,
  partyRoleDefaultsSchema,
  partySchema,
  partySortFieldSchema,
  partyTermDaysSchema,
  passwordResetConfirmSchema,
  passwordResetRequestSchema,
  percentageSchema,
  permissionCoverage,
  permissionSchema,
  platformAdministratorListResponseSchema,
  platformAdministratorSchema,
  positiveMoneySchema,
  postalCodeLookupParamsSchema,
  postalCodeLookupResponseSchema,
  postalCodeSchema,
  problemDetailsSchema,
  promotePlatformAdministratorRequestSchema,
  reconcileBankStatementLineRequestSchema,
  reconciliationCandidatesQuerySchema,
  reconciliationCandidatesResponseSchema,
  reconciliationSuggestionSchema,
  reconciliationTitleSchema,
  recordStatusSchema,
  recurrenceFrequencySchema,
  refineReportScope,
  reopenAccountingPeriodRequestSchema,
  reportExportFormatSchema,
  reportExportIdParamsSchema,
  reportExportKindSchema,
  reportExportListSchema,
  reportExportSchema,
  reportExportStatusSchema,
  reportScopeQueryShape,
  reportScopeSchema,
  roleCodeSchema,
  searchTermSchema,
  sessionRoleSchema,
  sessionSummarySchema,
  signedPercentSchema,
  sortOrderSchema,
  stateRegistrationSchema,
  statementPeriodSchema,
  taxIdSchema,
  taxRegimeSchema,
  titleAllocationInputSchema,
  titleAllocationRequestSchema,
  titleAllocationSchema,
  titleAttachmentListSchema,
  titleAttachmentParamsSchema,
  titleAttachmentSchema,
  titleDateFieldSchema,
  titleIdParamsSchema,
  titleImportFieldSchema,
  titleImportPreviewResponseSchema,
  titleImportRequestSchema,
  titleImportResultSchema,
  titleImportRowResultSchema,
  titleImportRowSchema,
  titleImportRowStatusSchema,
  titleImportWarningCodeSchema,
  titleKindSchema,
  titleListResponseSchema,
  titlePreviewItemSchema,
  titlePreviewResponseSchema,
  titleSchema,
  titleSortFieldSchema,
  titleStatusFilterSchema,
  titleStatusSchema,
  titleTotalsSchema,
  toCanonicalMoney,
  updateBankAccountRequestSchema,
  updateCompanyRequestSchema,
  updateCostCenterRequestSchema,
  updateFinanceSettingsRequestSchema,
  updateImportTemplateRequestSchema,
  updateLedgerAccountRequestSchema,
  updatePartyRequestSchema,
  updateTitleRequestSchema,
  updateUserAccessRequestSchema,
  userAccessSchema,
  userIdParamsSchema,
  userListResponseSchema,
  userSchema,
  userSortFieldSchema,
  userStatusSchema,
  uuidSchema,
  yearMonthSchema
};
//# sourceMappingURL=index.js.map